// src/views/Receipts.jsx  (Chunk 1/6)
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Split from 'react-split';
import './Receipts.css';
import { showToast, hideToast, updateToast } from '../utils/toast';
import '../components/PrintHelpDialog.css';
import {
  FaInbox, FaPlus, FaRegSave, FaTrash, FaEdit, FaSearch, FaTimes, FaUndo,
  FaHistory, FaSort, FaSortUp, FaSortDown, FaPrint, FaPaperclip, FaUpload, FaSync,
  FaDollarSign, FaEye, FaEnvelope, FaDownload, FaFolderOpen, FaColumns, FaTag, FaPaperPlane
} from 'react-icons/fa';

import ReceiptEditWorkspace from '../views/ReceiptEditWorkspace.jsx';
import PrintHelpDialog from '../components/PrintHelpDialog.jsx';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;
const LS_RCPT_COLWIDTHS_KEY = 'receiptsColWidths.v1';
const LS_RCPT_SORTS_KEY = 'receiptsSorts.v1';
const LS_RCPT_COLORDER_KEY = 'receiptsColOrder.v1';
const MIN_COL_W = 40;

// ---------- Date preset helpers (Mon-Sun week) ----------
const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};
const startOfWeekMon = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day); // Monday as first day
  x.setDate(x.getDate() + diff);
  return x;
};
const endOfWeekMon = (d) => { const s = startOfWeekMon(d); const e = new Date(s); e.setDate(e.getDate() + 6); return e; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear = (y) => new Date(y, 0, 1);
const endOfYear = (y) => new Date(y, 11, 31);

// ---------- Empty model ----------
const emptyReceipt = {
  receiptid: null,
  warehouseid: '',
  deleted: 0,
  date: '',
  userid: '',
  notes: '',
  clientid: '',
  status: 1,
  entrynumber: '',
  ponumber: '',
  palletized: 0,
  nonbillable: 0,
  test: 0,
};

export default function Receipts() {
  const navigate = useNavigate();

  // reusable confirm dialog
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Yes',
    cancelText: 'Cancel',
    onConfirm: null,
  });

  const openConfirm = (message, onConfirm, opts = {}) => {
    const { title = 'Confirm', confirmText = 'Yes', cancelText = 'Cancel' } = opts || {};
    setConfirmState({ open: true, title, message, confirmText, cancelText, onConfirm });
  };

  const closeConfirm = () => setConfirmState({
    open: false, title: 'Confirm', message: '', confirmText: 'Yes', cancelText: 'Cancel', onConfirm: null
  });

  // --- role helpers (client vs internal) ---
  function getCurrentUser() {
    try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; }
    catch { return {}; }
  }
  function isClientUser(u) {
    const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
    if (v === true) return true;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
    }
    return false;
  }
  const USER = getCurrentUser();
  const IS_CLIENT = isClientUser(USER);
  function getUserClientId(u) {
    try {
      const id = u.clientid ?? u.clientId ?? u.client_id;
      return (id || id === 0) ? String(id) : '';
    } catch { return ''; }
  }
  const LOCKED_CLIENT_ID = getUserClientId(USER);

  // UI pref getters
  const getInitialLimit = () => parseInt(window.localStorage.getItem('receiptsLimit') || DEFAULT_LIMIT, 10);
  const getInitialSort = () => JSON.parse(window.localStorage.getItem('receiptsSort') || `{"sortField":"date","sortOrder":"DESC"}`);
  const getInitialSorts = () => {
    try {
      const raw = window.localStorage.getItem(LS_RCPT_SORTS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.every(s => s && s.field && s.order)) return parsed;
    } catch {}
    const legacy = getInitialSort();
    return [{ field: legacy.sortField || 'date', order: legacy.sortOrder || 'DESC' }];
  };
  const getInitialInventorySort = () => JSON.parse(window.localStorage.getItem('inventorySort') || `{"sortField":"recvdate","sortOrder":"DESC"}`);
  const getInitialQ = () => localStorage.getItem('receiptsQ') || '';
  const getInitialFromDate = () => localStorage.getItem('receiptsFromDate') || '';
  const getInitialToDate = () => localStorage.getItem('receiptsToDate') || '';
  const getInitialShowDeleted = () => JSON.parse(localStorage.getItem('receiptsShowDeleted') || 'false');
  const getInitialClientName = () => localStorage.getItem('receiptsClientName') || '';
  const getInitialDatePreset = () => localStorage.getItem('receiptsDatePreset') || '';
  const getInitialSkuFilter = () => localStorage.getItem('receiptsSku') || '';
  const getInitialStatusIds = () => {
    try {
      const raw = localStorage.getItem('receiptsStatusIds');
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(String) : [];
    } catch { return []; }
  };

  // Filters + meta
  const [datePreset, setDatePreset] = useState(getInitialDatePreset());
  const [isTypingLimit, setIsTypingLimit] = useState(false);
  const [limit, setLimit] = useState(getInitialLimit());
  const [limitInput, setLimitInput] = useState(String(getInitialLimit()));
  const [receipts, setReceipts] = useState([]);
  const [meta, setMeta] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });
  const [q, setQ] = useState(getInitialQ());
  const [showDeleted, setShowDeleted] = useState(getInitialShowDeleted());
  const [fromDate, setFromDate] = useState(getInitialFromDate());
  const [toDate, setToDate] = useState(getInitialToDate());
  const [clientNameFilter, setClientNameFilter] = useState(getInitialClientName());
  const [skuFilter, setSkuFilter] = useState(getInitialSkuFilter());
  // Optional explicit client id filter (esp. for client users)
  const [clientIdFilter, setClientIdFilter] = useState(() => {
    try { return localStorage.getItem('receiptsClientId') || ''; } catch { return ''; }
  });
  const [statusIds, setStatusIds] = useState(getInitialStatusIds());
  // Billed filter: '', 'unbilled', 'billed'
  const [billedFilter, setBilledFilter] = useState(() => {
    try { return localStorage.getItem('receiptsBilled') || ''; } catch { return ''; }
  });
  useEffect(() => { try { localStorage.setItem('receiptsBilled', billedFilter || ''); } catch {} }, [billedFilter]);

  // Multi-sort state (persisted like Inventory) — define before effects that depend on it
  const [sorts, setSorts] = useState(getInitialSorts());
  useEffect(() => {
    try { window.localStorage.setItem(LS_RCPT_SORTS_KEY, JSON.stringify(sorts)); } catch {}
  }, [sorts]);

  // --- Inline placeholder for date filters inside the search box (omit date preset label) ---
  const searchPlaceholder = 'Search WR# / PO / Entry / Notes…';

  // Abort/race control for list fetch
  const receiptsFetchSeq = useRef(0);
  const receiptsFetchAbort = useRef(null);
  const lastFetchKeyRef = useRef('');

  // Single debounced effect covering all list filters/sorts to avoid duplicate reloads
  const lastQueryKeyRef = useRef('');
  useEffect(() => {
    try { localStorage.setItem('receiptsClientName', clientNameFilter); } catch {}
    try { localStorage.setItem('receiptsClientId', clientIdFilter || ''); } catch {}
    try { localStorage.setItem('receiptsSku', skuFilter || ''); } catch {}
    try { localStorage.setItem('receiptsStatusIds', JSON.stringify(statusIds)); } catch {}
    const queryKey = JSON.stringify({
      q: (q || '').trim(),
      client: (clientNameFilter || '').trim(),
      sku: (skuFilter || '').trim(),
      clientId: clientIdFilter || '',
      from: fromDate || '',
      to: toDate || '',
      deleted: !!showDeleted,
      status: (statusIds || []).slice().sort(),
      billed: billedFilter || '',
      sorts,
      limit,
    });
    const handle = setTimeout(() => {
      if (lastQueryKeyRef.current === queryKey) return; // prevent redundant fetch
      lastQueryKeyRef.current = queryKey;
      // Clear visible rows, keep last known count to avoid subtitle blink
      setReceipts([]);
      fetchReceipts(0, limit);
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, clientNameFilter, skuFilter, clientIdFilter, statusIds, sorts, showDeleted, fromDate, toDate, billedFilter, limit]);

  

  const [loadingList, setLoadingList] = useState(false);
  // Centered topbar toast; on completion, report count and keep a moment
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    const id = 'loading.receipts';
    if (loadingList && !prevLoadingRef.current) {
      showToast({ id, message: 'Loading receipts…', duration: 0, align: 'center', slideFrom: 'top', pill: true });
    }
    if (!loadingList && prevLoadingRef.current) {
      const total = Number((meta && meta.total) || 0);
      const count = Number.isFinite(total) && total > 0 ? total : (Array.isArray(receipts) ? receipts.length : 0);
      const formatted = Number(count).toLocaleString('en-US');
      // Use showToast with same id to reset timer reliably (dedupes in ToastHost)
      showToast({ id, message: `Loaded ${formatted} receipts.`, duration: 2500, align: 'center', slideFrom: 'top', pill: true });
    }
    prevLoadingRef.current = loadingList;
  }, [loadingList, receipts, meta?.total]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Multi-select & email modal (bulk)
  const [selectedReceiptIds, setSelectedReceiptIds] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailReceiptId, setEmailReceiptId] = useState(null); // single send id
  const [emailFields, setEmailFields] = useState({ to: '', cc: '', bcc: '', subject: '', message: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailAttachmentsLoading, setEmailAttachmentsLoading] = useState(false);
  const [emailAttachmentSelection, setEmailAttachmentSelection] = useState(new Set());
  const emailSingleReceiptId = useMemo(() => {
    if (!showEmailModal) return null;
    if (emailReceiptId) return emailReceiptId;
    if (!emailReceiptId && selectedReceiptIds.length === 1) return selectedReceiptIds[0];
    return null;
  }, [showEmailModal, emailReceiptId, selectedReceiptIds]);

  useEffect(() => {
    if (!showEmailModal || !emailSingleReceiptId) {
      setEmailAttachments([]);
      setEmailAttachmentSelection(new Set());
      setEmailAttachmentsLoading(false);
      return;
    }
    let cancelled = false;
    setEmailAttachmentsLoading(true);
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/receipts/${emailSingleReceiptId}/attachments`);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setEmailAttachments(list);
        setEmailAttachmentSelection(new Set());
      } catch (err) {
        if (!cancelled) {
          setEmailAttachments([]);
          setEmailAttachmentSelection(new Set());
        }
      } finally {
        if (!cancelled) setEmailAttachmentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showEmailModal, emailSingleReceiptId]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const singleClientOption = useMemo(
    () => (IS_CLIENT && clientOptions.length === 1 ? clientOptions[0] : null),
    [IS_CLIENT, clientOptions]
  );
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  // Reusable notification modal (success/failure)
  const [notify, setNotify] = useState({ open: false, type: 'success', title: '', message: '' });
  const openNotify = (type, title, message) => setNotify({ open: true, type, title, message });
  const closeNotify = () => setNotify(n => ({ ...n, open: false }));

  // form errors/refs
  const [warehouseError, setWarehouseError] = useState('');
  const warehouseSelectRef = useRef(null);
  const clientSelectRef = useRef(null);
  const [clientError, setClientError] = useState('');

  // Reusable status multi-select (same look/feel as Orders)
  function MultiSelectStatuses({ options = [], value = [], onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
      function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
      function onKey(e) { if (e.key === 'Escape') setOpen(false); }
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onKey);
      return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
    }, []);
    const isAll = (value || []).length === 0;
    const toggle = (id) => {
      const sid = String(id);
      if (value.includes(sid)) onChange(value.filter(v => v !== sid));
      else onChange([...(value || []), sid]);
    };
    const clear = () => onChange([]);
    const selectAll = () => onChange(options.map(o => String(o.id)));
    const selectedNames = options.filter(o => value.includes(String(o.id))).map(o => o.name);
    const summary = isAll ? 'All statuses' : (selectedNames.length <= 2 ? selectedNames.join(', ') : `${selectedNames.slice(0,2).join(', ')} +${selectedNames.length - 2}`);
    return (
      <div className="ms-container" ref={ref}>
        <button type="button" className={`ms-control ${open ? 'open' : ''}`} onClick={() => setOpen(v => !v)}
          aria-haspopup="listbox" aria-expanded={open} title="Filter by status">
          <span className={`ms-summary ${isAll ? 'muted' : ''}`}>{summary}</span>
          {!isAll && (
            <span className="ms-clear" onClick={(e) => { e.stopPropagation(); clear(); }} title="Clear" aria-label="Clear">×</span>
          )}
          <span className="ms-caret">▾</span>
        </button>
        {open && (
          <div className="ms-popover">
            <div className="ms-tools">
              <div className="ms-actions">
                <button type="button" className="btn" onClick={selectAll}>Select All</button>
                <button type="button" className="btn" onClick={clear}>Clear</button>
              </div>
            </div>
            <ul className="ms-list" role="listbox" aria-multiselectable="true">
              {options.length ? options.map(o => {
                const checked = value.includes(String(o.id));
                return (
                  <li key={o.id}>
                    <label className="ms-option">
                      <input type="checkbox" checked={checked} onChange={() => toggle(o.id)} />
                      <span>{o.name}</span>
                    </label>
                  </li>
                );
              }) : <li className="ms-empty">No statuses</li>}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // track selected synchronously for async waits
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  // waitUntil helper (prevents races)
  function waitUntil(fn, { tries = 12, delay = 35 } = {}) {
    return new Promise((resolve) => {
      let n = 0;
      const tick = () => {
        if (fn()) return resolve(true);
        if (++n >= tries) return resolve(false);
        setTimeout(tick, delay);
      };
      tick();
    });
  }

  // Bottom panes
  const [bottomPaneMode, setBottomPaneMode] = useState(null);
  const [showInventoryPane, setShowInventoryPane] = useState(false);

  // Inventory pane
  const [inventoryRows, setInventoryRows] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [sortInventoryField, setSortInventoryField] = useState(getInitialInventorySort().sortField);
  const [sortInventoryOrder, setSortInventoryOrder] = useState(getInitialInventorySort().sortOrder);
  // Inventory table: resizable columns (like Inventory/Orders)
  const RCPT_INV_COLS = [
    'select','pallet_number','recvdate','storage_device','sku','item_description','location','warehouse_area','description','partnumber','expiration','lotnum','serial','ponum','packaging','qtyunits','qtyperunit','pieces','qtyunitsonhand','piecesonhand','unit_volume','total_units_volume','item_volume','available_item_volume'
  ];
  const LS_RCPT_INV_COLS = 'receiptInvColWidths.v1';
  // Default widths for the bottom Inventory table
  const DEFAULT_RCPT_INV_WIDTHS = {
    select: 34,
    pallet_number: 120,
    recvdate: 110,
    storage_device: 160,
    sku: 140,
    item_description: 220,
    location: 120,
    warehouse_area: 140,
    description: 220,
    partnumber: 140,
    expiration: 110,
    lotnum: 120,
    serial: 140,
    ponum: 120,
    packaging: 140,
    qtyunits: 140,
    qtyperunit: 120,
    pieces: 140,
    qtyunitsonhand: 160,
    piecesonhand: 160,
    unit_volume: 120,
    total_units_volume: 160,
    item_volume: 120,
    available_item_volume: 180,
  };
  const getInitialRcptInvWidths = () => {
    try {
      const raw = localStorage.getItem(LS_RCPT_INV_COLS);
      const j = raw ? JSON.parse(raw) : {};
      return (j && typeof j === 'object' && Object.keys(j).length) ? j : { ...DEFAULT_RCPT_INV_WIDTHS };
    } catch {
      return { ...DEFAULT_RCPT_INV_WIDTHS };
    }
  };
  const [rcptInvColWidths, setRcptInvColWidths] = useState(getInitialRcptInvWidths());
  const rcptInvHeaderRef = useRef(null);
  const rcptInvTableRef = useRef(null);
  const rcptInvResizeRef = useRef({ key: null, startX: 0, startW: 0 });
  useEffect(() => { try { localStorage.setItem(LS_RCPT_INV_COLS, JSON.stringify(rcptInvColWidths)); } catch {} }, [rcptInvColWidths]);
  const ensureRcptInvWidthsLocked = () => {
    if (Object.keys(rcptInvColWidths||{}).length === RCPT_INV_COLS.length) return;
    const row = rcptInvHeaderRef.current; if (!row) return;
    const next = {}; RCPT_INV_COLS.forEach((k,i)=>{ const th = row.children?.[i]; if (th) next[k]=th.offsetWidth; });
    setRcptInvColWidths(next);
  };
  const onRcptInvMove = (e) => { const {key,startX,startW}=rcptInvResizeRef.current; if(!key) return; const nextW=Math.max(MIN_COL_W, startW + (e.clientX-startX)); setRcptInvColWidths(prev=>({...prev,[key]:nextW})); e.preventDefault(); };
  const onRcptInvUp = () => { rcptInvResizeRef.current={ key:null,startX:0,startW:0 }; try{document.body.classList.remove('col-resizing');}catch{} window.removeEventListener('mousemove', onRcptInvMove); window.removeEventListener('mouseup', onRcptInvUp); };
  const startRcptInvResize = (key,e) => { ensureRcptInvWidthsLocked(); const th=e.currentTarget.parentElement; rcptInvResizeRef.current={ key, startX:e.clientX, startW: th.offsetWidth }; try{document.body.classList.add('col-resizing');}catch{} window.addEventListener('mousemove', onRcptInvMove); window.addEventListener('mouseup', onRcptInvUp); e.preventDefault(); e.stopPropagation(); };

  // History pane
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Attachments pane
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachDesc, setAttachDesc] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [splitSizes, setSplitSizes] = useState(() => JSON.parse(localStorage.getItem('receipts-split-sizes') || '[60,40]'));

  // ===== Columns (orderable + resizable, persisted) =====
  // Default widths used on first load when no saved widths exist
  const DEFAULT_RCPT_COLWIDTHS = {
    select: 34,
    actions: 140,
    receiptid: 90,
    clientname: 220,
    status: 120,
    createdby: 140,
    date: 120,
    warehouse: 180,
    ponumber: 140,
    entrynumber: 140,
    // notes is last/flexible; include for completeness
    notes: 320,
  };

  const baseReceiptColumns = [
    { key: 'select', defaultWidth: DEFAULT_RCPT_COLWIDTHS.select },
    { key: 'actions', defaultWidth: DEFAULT_RCPT_COLWIDTHS.actions },
    { key: 'receiptid', sort: 'receiptid', label: 'WR #', defaultWidth: DEFAULT_RCPT_COLWIDTHS.receiptid },
    { key: 'clientname', sort: 'clientname', label: 'Client', defaultWidth: DEFAULT_RCPT_COLWIDTHS.clientname },
    { key: 'status', sort: 'status', label: 'Status', defaultWidth: DEFAULT_RCPT_COLWIDTHS.status },
    { key: 'createdby', sort: 'createdby', label: 'Created By', defaultWidth: DEFAULT_RCPT_COLWIDTHS.createdby },
    { key: 'date', sort: 'date', label: 'Date', defaultWidth: DEFAULT_RCPT_COLWIDTHS.date },
    // Backend expects sort on 'warehouseid' for warehouse name
    { key: 'warehouse', sort: 'warehouseid', label: 'Warehouse', defaultWidth: DEFAULT_RCPT_COLWIDTHS.warehouse },
    { key: 'ponumber', sort: 'ponumber', label: 'PO #', defaultWidth: DEFAULT_RCPT_COLWIDTHS.ponumber },
    { key: 'entrynumber', sort: 'entrynumber', label: 'Entry #', defaultWidth: DEFAULT_RCPT_COLWIDTHS.entrynumber },
    { key: 'notes', label: 'Notes', defaultWidth: DEFAULT_RCPT_COLWIDTHS.notes },
  ];
  const getInitialReceiptColumns = () => {
    let order = [];
    try { order = JSON.parse(localStorage.getItem(LS_RCPT_COLORDER_KEY) || '[]'); } catch {}
    const byKey = Object.fromEntries(baseReceiptColumns.map(c => [c.key, c]));
    const picked = order.map(k => byKey[k]).filter(Boolean);
    const remaining = baseReceiptColumns.filter(c => !picked.find(p => p.key === c.key));
    return [...picked, ...remaining];
  };
  const [receiptColumns, setReceiptColumns] = useState(getInitialReceiptColumns());
  useEffect(() => {
    try { localStorage.setItem(LS_RCPT_COLORDER_KEY, JSON.stringify(receiptColumns.map(c => c.key))); } catch {}
  }, [receiptColumns]);
  const getInitialRcptColWidths = () => {
    try {
      const raw = localStorage.getItem(LS_RCPT_COLWIDTHS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {}
    // No saved sizes: seed with sensible defaults
    return { ...DEFAULT_RCPT_COLWIDTHS };
  };
  const [rcptColWidths, setRcptColWidths] = useState(getInitialRcptColWidths());
  const [rcptAutoFit, setRcptAutoFit] = useState(false);
  const rcptResizeState = useRef({ key: null, startX: 0, startW: 0 });
  const rcptResizedRecently = useRef(false);
  const rcptHeaderRowRef = useRef(null);
  const rcptTableRef = useRef(null);
  const dragRcptColKeyRef = useRef(null);

  // Measure a column's ideal width from header + visible cells
  const measureRcptColWidth = (colIdx) => {
    try {
      let maxW = 0;
      const headerRow = rcptHeaderRowRef.current;
      if (headerRow?.children?.[colIdx]) {
        const th = headerRow.children[colIdx];
        maxW = Math.max(maxW, Math.ceil(Math.max(th.scrollWidth, th.offsetWidth)));
      }
      const tbl = rcptTableRef.current;
      if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[colIdx];
          if (cell) maxW = Math.max(maxW, Math.ceil(Math.max(cell.scrollWidth, cell.offsetWidth)));
        }
      }
      return Math.max(MIN_COL_W, Math.min(800, maxW));
    } catch {
      return 120;
    }
  };

  // Auto-fit all receipt columns to content
  const autoFitAllRcptCols = () => {
    const headerRow = rcptHeaderRowRef.current;
    if (!headerRow || !receiptColumns?.length) return;
    const next = {};
    for (let i = 0; i < receiptColumns.length; i++) {
      const key = receiptColumns[i].key;
      next[key] = measureRcptColWidth(i);
    }
    setRcptColWidths(next);
  };

  // Reset columns: order + widths (auto-fit to largest content)
  const resetReceiptColumns = () => {
    try {
      localStorage.removeItem(LS_RCPT_COLORDER_KEY);
      localStorage.removeItem(LS_RCPT_COLWIDTHS_KEY);
    } catch {}
    setReceiptColumns([...baseReceiptColumns]);
    // Enter auto-fit mode so browser lays out content naturally
    setRcptAutoFit(true);
    // Measure after next paint(s), then freeze widths and exit auto-fit
    const afterPaint = () => {
      const headerRow = rcptHeaderRowRef.current;
      const hasRows = !!rcptTableRef.current?.tBodies?.[0]?.rows?.length;
      if (!headerRow || !hasRows) { requestAnimationFrame(afterPaint); return; }
      const next = {};
      const cols = baseReceiptColumns;
      for (let i = 0; i < cols.length; i++) {
        const key = cols[i].key;
        next[key] = measureRcptColWidth(i);
      }
      setRcptColWidths(next);
      // allow a tick so widths apply before turning off auto-fit
      setTimeout(() => setRcptAutoFit(false), 0);
    };
    requestAnimationFrame(afterPaint);
  };

  // persist widths whenever they change
  useEffect(() => {
    try { localStorage.setItem(LS_RCPT_COLWIDTHS_KEY, JSON.stringify(rcptColWidths)); } catch {}
  }, [rcptColWidths]);

  const ensureRcptWidthsLocked = () => {
    const row = rcptHeaderRowRef.current; if (!row) return;
    const ths = Array.from(row.children || []);
    if (!ths.length) return;
    if (Object.keys(rcptColWidths || {}).length === ths.length) return;
    const next = {};
    receiptColumns.forEach((c, idx) => {
      const th = ths[idx]; if (th && c?.key) next[c.key] = th.offsetWidth;
    });
    setRcptColWidths(next);
  };

  const onRcptResizeMove = (e) => {
    const { key, startX, startW } = rcptResizeState.current;
    if (!key) return;
    const delta = e.clientX - startX;
    const nextW = Math.max(MIN_COL_W, startW + delta);
    setRcptColWidths(prev => ({ ...prev, [key]: nextW }));
    e.preventDefault();
  };
  const onRcptResizeUp = () => {
    rcptResizeState.current = { key: null, startX: 0, startW: 0 };
    try { document.body.classList.remove('col-resizing'); } catch {}
    window.removeEventListener('mousemove', onRcptResizeMove);
    window.removeEventListener('mouseup', onRcptResizeUp);
    rcptResizedRecently.current = true; setTimeout(() => { rcptResizedRecently.current = false; }, 150);
  };
  const startRcptResize = (key, e) => {
    ensureRcptWidthsLocked();
    const th = e.currentTarget.parentElement;
    rcptResizeState.current = { key, startX: e.clientX, startW: th.offsetWidth };
    try { document.body.classList.add('col-resizing'); } catch {}
    window.addEventListener('mousemove', onRcptResizeMove);
    window.addEventListener('mouseup', onRcptResizeUp);
    e.preventDefault(); e.stopPropagation();
  };

  const autoFitRcptCol = (key) => {
    try {
      const idx = receiptColumns.findIndex(c => c.key === key);
      if (idx === -1) return;
      let maxW = 0;
      if (rcptHeaderRowRef.current?.children[idx]) {
        maxW = Math.max(maxW, Math.ceil(rcptHeaderRowRef.current.children[idx].scrollWidth));
      }
      const tbl = rcptTableRef.current;
      if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[idx];
          if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      const w = Math.max(MIN_COL_W, Math.min(800, maxW));
      setRcptColWidths(prev => ({ ...prev, [key]: w }));
    } catch {}
  };

  // Ensure initial column widths are locked from the rendered header so
  // <colgroup> has values (prevents width:0 sticky glitches)
  useEffect(() => {
    const t = setTimeout(() => ensureRcptWidthsLocked(), 0);
    return () => clearTimeout(t);
  }, [receipts.length, receiptColumns.length]);

  // Print dialog (new)
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printForReceiptId, setPrintForReceiptId] = useState(null);

  // New Receipt (separate dialog, independent of editing)
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  // Legacy print-help
  const [showPrintOpts, setShowPrintOpts] = useState(false);
  const [printForReceipt, setPrintForReceipt] = useState(null);

  const getInitialIncludeMoved = () => (localStorage.getItem('printIncludeMoved') === '1');
  const [includeMoved, setIncludeMoved] = useState(getInitialIncludeMoved());
  const originalOnly = useMemo(() => !includeMoved, [includeMoved]);
  useEffect(() => { localStorage.setItem('printIncludeMoved', includeMoved ? '1' : '0'); }, [includeMoved]);
  const getInitialPrintSort = () => localStorage.getItem('printSortBy') || 'default';
  const [printSortBy, setPrintSortBy] = useState(getInitialPrintSort());
  useEffect(() => {
    try { localStorage.setItem('printSortBy', printSortBy); } catch {}
  }, [printSortBy]);
  const getInitialDownloadScope = () => {
    try { return localStorage.getItem('receiptsDownloadScope') || 'original'; }
    catch { return 'original'; }
  };
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadInventoryScope, setDownloadInventoryScope] = useState(getInitialDownloadScope());
  useEffect(() => {
    try { localStorage.setItem('receiptsDownloadScope', downloadInventoryScope); } catch {}
  }, [downloadInventoryScope]);

  const page = useMemo(() => Math.floor((meta.offset || 0) / (limit || DEFAULT_LIMIT)) + 1, [meta.offset, limit]);
  const lastPage = useMemo(() => Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT))), [meta.total, limit]);

  const sortedInventoryRows = useMemo(() => {
    if (!inventoryRows.length) return [];
    const rows = [...inventoryRows];
    rows.sort((a, b) => {
      const dir = sortInventoryOrder === 'ASC' ? 1 : -1;
      const A = a[sortInventoryField];
      const B = b[sortInventoryField];
      const aNum = typeof A === 'number' ? A : Date.parse(A) || A;
      const bNum = typeof B === 'number' ? B : Date.parse(B) || B;
      if (aNum > bNum) return dir;
      if (aNum < bNum) return -dir;
      return 0;
    });
    return rows;
  }, [inventoryRows, sortInventoryField, sortInventoryOrder]);

  // Selection for pallet label printing
  const [selectedInvIds, setSelectedInvIds] = useState(() => new Set());
  const toggleInvSelected = (id) => {
    setSelectedInvIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllInv = (checked) => {
    if (checked) setSelectedInvIds(new Set(sortedInventoryRows.map(r => r.inventoryid)));
    else setSelectedInvIds(new Set());
  };

  const uniquePalletNumbers = useMemo(() => {
    const nums = sortedInventoryRows.map(r => r.pallet_number).filter(pn => pn != null && pn !== '');
    return new Set(nums).size;
  }, [sortedInventoryRows]);

  // Totals for Inventory bottom-pane (Qty Boxes, Qty Unit Available, Items/Units Avail)
  const invTotals = useMemo(() => {
    const sums = { qtyunits: 0, pieces: 0, qtyunitsonhand: 0, piecesonhand: 0 };
    for (const r of sortedInventoryRows) {
      const u = Number(r?.qtyunits) || 0;
      const per = Number(r?.qtyperunit) || 0;
      const pieces = (() => {
        const d = Number(r?.pieces);
        if (Number.isFinite(d) && d > 0) return d;
        return (Number.isFinite(u) && Number.isFinite(per)) ? (u * per) : 0;
      })();
      sums.qtyunits += u;
      sums.pieces += pieces;
      sums.qtyunitsonhand += Number(r?.qtyunitsonhand) || 0;
      sums.piecesonhand += Number(r?.piecesonhand) || 0;
    }
    return sums;
  }, [sortedInventoryRows]);

  // Active highlight for a single inventory row (like Receipts list)
  const [activeInvRowId, setActiveInvRowId] = useState(null);
  useEffect(() => { setActiveInvRowId(null); }, [selected?.receiptid]);

  const fetchJSON = async (url, opts = {}) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { const b = await res.json(); if (b?.error) msg = b.error; } catch { }
      throw new Error(msg);
    }
    return res.json();
  };


  // ===== Agent-driven Create Receipt wiring (robust) =====
  // Parse advanced tokens from single search box
  const parseReceiptSearch = (raw) => {
    const out = { q: '', receiptid: '', statusid: '', createdby: '', warehouseid: '' };
    if (!raw) return out;
    const tokens = raw.trim().split(/\s+/);
    const leftovers = [];
    for (let i = 0; i < tokens.length; i++) {
      const tok = tokens[i];
      // Allow two-token patterns like "wr 123" or "wr# 123" or "receipt 123"
      if (/^(wr|wr#|receipt|receipt#)$/i.test(tok)) {
        const next = tokens[i + 1] || '';
        const m = String(next).match(/(\d+)/);
        if (m && m[1]) { out.receiptid = m[1]; i += 1; continue; }
      }
      // Single-token compact: wr123 or wr#123
      const wr = tok.match(/^wr#?(\d+)$/i);
      if (wr) { out.receiptid = wr[1]; continue; }
      // key:value forms
      const [kRaw, ...rest] = tok.split(':');
      if (!rest.length) { leftovers.push(tok); continue; }
      const k = (kRaw || '').toLowerCase();
      const v = rest.join(':');
      if (['receipt', 'receiptid', 'wr'].includes(k)) {
        const m2 = v.match(/(\d+)/); if (m2) out.receiptid = m2[1]; else leftovers.push(tok); continue;
      }
      if (['status', 'st', 'statusid'].includes(k)) {
        const n = Number(v);
        if (!Number.isNaN(n)) { out.statusid = String(n); continue; }
        const s = (statusOptions || []).find(s => (s.name || '').toLowerCase() === v.toLowerCase());
        if (s) out.statusid = String(s.statusid || s.id); else leftovers.push(tok);
        continue;
      }
      if (['by', 'createdby', 'user'].includes(k)) { out.createdby = v; continue; }
      if (['wh', 'warehouse', 'warehouseid'].includes(k)) {
        const n = Number(v);
        if (!Number.isNaN(n)) { out.warehouseid = String(n); continue; }
        const w = (warehouseOptions || []).find(w => (w.name || '').toLowerCase() === v.toLowerCase());
        if (w) out.warehouseid = String(w.id); else leftovers.push(tok);
        continue;
      }
      leftovers.push(tok);
    }
    out.q = leftovers.join(' ');
    return out;
  };

  // ---------- Lookups loaded flag ----------
  const lookupsLoaded = useMemo(
    () => (warehouseOptions?.length || 0) > 0 && (clientOptions?.length || 0) > 0,
    [warehouseOptions, clientOptions]
  );

  const pendingCreateRef = useRef(null);

  // Accept payload in any of these shapes: e.detail | e.detail.payload | e.detail.action.payload
  function normalizeAgentPayload(pAny = {}) {
    const p1 = pAny?.payload ?? pAny;
    const p = p1?.action?.payload ?? p1;

    const first = (...vals) => vals.find(v => v !== undefined && v !== null && v !== '');

    // IDs in both styles & initialReceipt
    const warehouseId = first(p?.warehouseid, p?.warehouseId, p?.initialReceipt?.warehouseid, p?.initialReceipt?.warehouseId);
    const clientId = first(p?.clientid, p?.clientId, p?.initialReceipt?.clientid, p?.initialReceipt?.clientId);

    // Names anywhere
    const warehouseName = first(p?.warehousename, p?.warehouseName, p?.resolvedNames?.warehouse, p?.initialReceipt?.warehouse);
    const clientName = first(p?.clientname, p?.clientName, p?.resolvedNames?.client, p?.initialReceipt?.client);

    // PO / Entry / Notes from any slot
    const po = first(p?.po, p?.ponumber, p?.initialReceipt?.po, p?.initialReceipt?.ponumber, '');
    const entry = first(p?.entry, p?.entrynumber, p?.initialReceipt?.entry, p?.initialReceipt?.entrynumber, '');
    const notes = first(p?.notes, p?.initialReceipt?.notes, '');

    return { warehouseId, clientId, warehouseName, clientName, po, entry, notes };
  }

  // Fallback name→id resolver
  function resolveIdByName(options, name) {
    if (!name || !Array.isArray(options) || !options.length) return null;
    const norm = (s) => String(s || '').toLowerCase()
      .replace(/\b(warehouse|wh|client|customer)\b/g, '')
      .replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    const target = norm(name);
    const scored = options.map(o => {
      const n = norm(o.name);
      let s = 0;
      if (n === target) s += 100;
      else if (n.startsWith(target)) s += 60;
      else if (n.includes(target)) s += 30;
      const T = new Set(target.split(' ').filter(Boolean));
      const N = new Set(n.split(' ').filter(Boolean));
      let ov = 0; T.forEach(t => N.has(t) && ov++);
      s += ov * 10;
      return { id: o.id, score: s };
    }).sort((a, b) => b.score - a.score);
    return (scored[0]?.score ?? 0) >= 35 ? scored[0].id : null;
  }

  async function processAgentCreateReceipt(rawDetail) {
    if (IS_CLIENT) {
      alert('Your role does not allow creating receipts.');
      return;
    }
    if (!rawDetail) {
      console.log('[Agent->Receipts] Error: No payload received');
      return;
    }
    console.log('[Agent->Receipts] Raw payload:', JSON.stringify(rawDetail, null, 2));
    const detail = rawDetail?.payload ?? rawDetail?.action?.payload ?? rawDetail;
    const userId = Number.isFinite(+detail.userId) ? Number(detail.userId) : null;
    const whId = Number.isFinite(+detail.warehouseId) ? Number(detail.warehouseId) :
      Number.isFinite(+detail.warehouseid) ? Number(detail.warehouseid) : null;
    const clId = Number.isFinite(+detail.clientId) ? Number(detail.clientId) :
      Number.isFinite(+detail.clientid) ? Number(detail.clientid) : null;
    const warehouseName = detail.warehouseName || detail.resolvedNames?.warehouse || '';
    const clientName = detail.clientName || detail.resolvedNames?.client || '';
    const po = detail.po || detail.ponumber || '';
    const entry = detail.entry || detail.entrynumber || '';
    const notes = detail.notes || '';
    console.log('[Agent->Receipts] Extracted: userId=', userId, 'warehouseId=', whId, 'clientId=', clId, 'warehouseName=', warehouseName, 'clientName=', clientName, 'po=', po, 'entry=', entry, 'notes=', notes);
    if (!userId || !whId || !clId) {
      console.log('[Agent->Receipts] Error: Missing valid IDs (userId=', userId, 'warehouseId=', whId, 'clientId=', clId, ')');
      alert('Cannot create receipt: invalid user, warehouse, or client ID.');
      return;
    }
    setEditing(false);
    setBottomPaneMode(null);
    setCreating(true); // For UI consistency
    const hydrated = {
      ...emptyReceipt,
      // Use local date (YYYY-MM-DD) to avoid UTC off-by-one
      date: fmtYMD(new Date()),
      userid: userId,
      warehouseid: whId,
      clientid: clId,
      ponumber: po,
      entrynumber: entry,
      notes: notes,
    };
    console.log('[Agent->Receipts] Hydrated:', JSON.stringify(hydrated, null, 2));
    setSelected(hydrated);
    // Call saveReceipt directly with hydrated object
    await saveReceipt(hydrated);
  }

  // Listen for agent events (unwrap shapes)
  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail ?? {};
      const unwrapped = d?.payload ?? d?.action?.payload ?? d;
      processAgentCreateReceipt(unwrapped);
    };
    window.addEventListener('agent:createReceipt', handler);
    return () => window.removeEventListener('agent:createReceipt', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupsLoaded, warehouseOptions, clientOptions, IS_CLIENT]);

  // Replay queued agent request once lookups are ready
  useEffect(() => {
    if (pendingCreateRef.current && lookupsLoaded) {
      const d = pendingCreateRef.current; pendingCreateRef.current = null;
      processAgentCreateReceipt(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupsLoaded]);

  // Race-safe fetchReceipts with abort+sequence
  const fetchReceipts = async (offset = 0, customLimit = limit, opts = {}) => {
    const { recenter = true } = opts || {};
    const thisSeq = ++receiptsFetchSeq.current;
    // Build a dedupe key for the request
    const parsedForKey = parseReceiptSearch(q);
    const reqKey = JSON.stringify({
      off: offset || 0,
      lim: customLimit || DEFAULT_LIMIT,
      sorts,
      q: parsedForKey.q || '',
      from: fromDate || '',
      to: toDate || '',
      deleted: !!showDeleted,
      clientname: !IS_CLIENT ? (clientNameFilter || '') : '',
      // include explicit client selection for dedupe only for client users
      cid: IS_CLIENT ? String(clientIdFilter || LOCKED_CLIENT_ID || '') : '',
      status: (statusIds || []).slice().sort(),
      billed: billedFilter || '',
      sku: (skuFilter || '').trim(),
    });
    if (loadingList && lastFetchKeyRef.current === reqKey) return; // prevent identical concurrent fetches
    lastFetchKeyRef.current = reqKey;

    if (receiptsFetchAbort.current) { try { receiptsFetchAbort.current.abort(); } catch { } }
    const controller = new AbortController();
    receiptsFetchAbort.current = controller;

    setLoadingList(true);
    try {
      const parsed = parseReceiptSearch(q);
      const params = new URLSearchParams({
        limit: customLimit, offset,
      });
      const sortParam = (Array.isArray(sorts) && sorts.length)
        ? sorts.map(s => `${s.field}:${s.order}`).join(',')
        : '';
      if (sortParam) params.set('sort', sortParam);
      if (parsed.q) params.set('q', parsed.q);
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      if (!showDeleted) params.set('deleted', 0);
      if (parsed.receiptid) params.set('receiptid', parsed.receiptid);
      if (parsed.statusid) params.set('statusid', parsed.statusid);
      if (parsed.createdby) params.set('createdby', parsed.createdby);
      if (parsed.warehouseid) params.set('warehouseid', parsed.warehouseid);
      if (!IS_CLIENT && clientNameFilter) params.set('clientname', clientNameFilter);
      // Only send clientid for client users (internal users use name filter or explicit params elsewhere)
      if (IS_CLIENT && clientIdFilter) params.set('clientid', String(clientIdFilter));
      if (Array.isArray(statusIds) && statusIds.length) params.set('statusids', statusIds.join(','));
      if (skuFilter) params.set('sku', skuFilter);

      // billed/unbilled (server accepts 'billed'/'unbilled' or 1/0)
      if (billedFilter === 'unbilled') params.set('hasinvoice', 'unbilled');
      else if (billedFilter === 'billed') params.set('hasinvoice', 'billed');

      const json = await fetchJSON(`${API}/api/receipts?${params.toString()}`, { signal: controller.signal });
      if (thisSeq !== receiptsFetchSeq.current) return;

      const total = Number(json?.meta?.total || 0);
      const safeLimit = Number.isFinite(+customLimit) && +customLimit > 0 ? +customLimit : DEFAULT_LIMIT;
      const requestedPage = Math.floor((offset || 0) / safeLimit) + 1;
      const computedLastPage = Math.max(1, Math.ceil(total / safeLimit));

      if (recenter && requestedPage > computedLastPage) {
        const newOffset = Math.max(0, (computedLastPage - 1) * safeLimit);
        setMeta({ total, limit: safeLimit, offset: newOffset });
        await fetchReceipts(newOffset, safeLimit, { recenter: false });
        return;
      }

      // When filtering by Unbilled, exclude any Non-billable receipts from the list
      const serverRows = Array.isArray(json.data) ? json.data : [];
      const filteredRows = (billedFilter === 'unbilled')
        ? serverRows.filter(r => Number(r?.nonbillable || 0) !== 1)
        : serverRows;
      setReceipts(filteredRows);
      setMeta(json.meta || { total, limit: safeLimit, offset });
    } catch (e) {
      if (e?.name === 'AbortError') return;
      if (thisSeq !== receiptsFetchSeq.current) return;
      setReceipts([]); setMeta({ total: 0, limit: customLimit, offset });
    } finally {
      // Clear loading if this request actually completed (was not aborted)
      if (!controller.signal.aborted) setLoadingList(false);
    }
  };

  // ---------- Fetchers ----------
  const fetchInventory = async (receiptId) => {
    if (!receiptId) { setInventoryRows([]); return; }
    setInventoryLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/inventory/by-receipt/${receiptId}`);
      setInventoryRows(data || []);
    } catch { setInventoryRows([]); } finally { setInventoryLoading(false); }
  };

  const fetchStatusOptions = async () => {
    try { const json = await fetchJSON(`${API}/api/receipts/status/all`); setStatusOptions(Array.isArray(json) ? json : []); }
    catch { setStatusOptions([]); }
  };

  const fetchWarehouseOptions = async () => {
    if (IS_CLIENT) { setWarehouseOptions([]); return; }
    try {
      // Use read-only filters endpoint to allow non-admin internal users
      const json = await fetchJSON(`${API}/api/reports/filters/warehouses`);
      const opts = Array.isArray(json) ? json.map(w => ({ id: w.id, name: w.name })) : [];
      setWarehouseOptions(opts);
    } catch { setWarehouseOptions([]); }
  };

  const fetchClientOptions = async () => {
    try {
      if (IS_CLIENT) {
        const res = await fetch(`${API}/api/reports/filters/clients`);
        const data = res.ok ? await res.json() : [];
        const opts = (Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), name: c.name }));
        setClientOptions(opts);
        // If current selection is invalid, clear it
        if (clientIdFilter && !opts.find(o => String(o.id) === String(clientIdFilter))) setClientIdFilter('');
        if (opts.length === 1) {
          setClientIdFilter(String(opts[0]?.id || ''));
        }
      } else {
        const json = await fetchJSON(`${API}/api/clients?limit=1000`);
        let opts = Array.isArray(json?.data) ? json.data.map(c => ({ id: c.id, name: c.name })) : [];
        setClientOptions(opts);
      }
    } catch { setClientOptions([]); }
  };

  // Attachments helpers
  const removePendingFile = (index) => setPendingFiles(prev => prev.filter((_, i) => i !== index));
  const fetchReceiptAttachments = async (receiptId) => {
    if (!receiptId) { setAttachments([]); return; }
    setAttachmentsLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/receipts/${receiptId}/attachments`);
      setAttachments(Array.isArray(data) ? data : []);
    } catch { setAttachments([]); } finally { setAttachmentsLoading(false); }
  };

  const uploadFiles = async () => {
    if (!selected?.receiptid || !pendingFiles?.length) return;
    const fd = new FormData();
    pendingFiles.forEach(f => fd.append('files', f));
    if (attachDesc) fd.append('description', attachDesc);
    try {
      await fetch(`${API}/api/receipts/${selected.receiptid}/attachments`, { method: 'POST', body: fd });
      setAttachDesc(''); setPendingFiles([]); await fetchReceiptAttachments(selected.receiptid);
    } catch (e) { alert('Upload failed: ' + e.message); }
  };

  const deleteAttachment = async (attId) => {
    if (!selected?.receiptid) return;

    openConfirm(`Delete this attachment?`, async () => {
      try {
        await fetchJSON(`${API}/api/receipts/${selected.receiptid}/attachments/${attId}`, { method: 'DELETE' });
        await fetchReceiptAttachments(selected.receiptid);
      } catch (e) { alert('Delete failed: ' + e.message); }
    });
  };

  const onDrop = (e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer?.files; if (f && f.length) setPendingFiles(Array.from(f)); };
  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);

  // Generate 4x6 pallet labels for selected inventory rows
  const printPalletLabels = async () => {
    const ids = Array.from(selectedInvIds);
    if (!ids.length) { alert('Select one or more inventory rows to print pallet labels.'); return; }
    try {
      const res = await fetch(`${API}/api/inventory/labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventoryIds: ids })
      });
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message || 'Failed to generate pallet labels');
    }
  };

  // Persist filters
  useEffect(() => { localStorage.setItem('receiptsQ', q); }, [q]);
  useEffect(() => { localStorage.setItem('receiptsFromDate', fromDate); }, [fromDate]);
  useEffect(() => { localStorage.setItem('receiptsToDate', toDate); }, [toDate]);
  useEffect(() => { localStorage.setItem('receiptsShowDeleted', JSON.stringify(showDeleted)); }, [showDeleted]);

  // When switching client filter, clear selection and close bottom panes to avoid stale context
  useEffect(() => {
    setSelected(null);
    setBottomPaneMode(null);
    setInventoryRows([]);
    setHistory([]);
    setAttachments([]);
  }, [clientIdFilter]);

  // Pane visibility
  useEffect(() => { setShowInventoryPane(bottomPaneMode !== null); }, [bottomPaneMode]);

  // Load pane data
  useEffect(() => {
    if (!selected?.receiptid) { setHistory([]); setInventoryRows([]); setAttachments([]); return; }
    if (bottomPaneMode === 'inventory') fetchInventory(selected.receiptid);
    else if (bottomPaneMode === 'history') {
      setHistoryLoading(true);
      fetchJSON(`${API}/api/receipts/${selected.receiptid}/history`)
        .then(rows => setHistory(Array.isArray(rows) ? rows : []))
        .catch(() => setHistory([]))
        .finally(() => setHistoryLoading(false));
    } else if (bottomPaneMode === 'attachments') fetchReceiptAttachments(selected.receiptid);
  }, [selected?.receiptid, bottomPaneMode]);

  // Reset inventory selections when switching receipts or inventory changes
  useEffect(() => { setSelectedInvIds(new Set()); }, [selected?.receiptid]);
  useEffect(() => { setSelectedInvIds(new Set()); }, [inventoryRows]);

  // Load lookups once
  useEffect(() => {
    fetchStatusOptions();
    fetchClientOptions();
    if (!IS_CLIENT) fetchWarehouseOptions();
  }, []);
  // keep legacy single-sort key updated for any other readers
  useEffect(() => {
    const primary = Array.isArray(sorts) && sorts[0] ? sorts[0] : { field: 'date', order: 'DESC' };
    try { localStorage.setItem('receiptsSort', JSON.stringify({ sortField: primary.field, sortOrder: primary.order })); } catch {}
  }, [sorts]);
  useEffect(() => { localStorage.setItem('inventorySort', JSON.stringify({ sortField: sortInventoryField, sortOrder: sortInventoryOrder })); }, [sortInventoryField, sortInventoryOrder]);

  // Topbar: show edit title when editing a receipt; otherwise show Receipts ( # found )
  useEffect(() => {
    if (editing && selected?.receiptid) {
      window.dispatchEvent(new CustomEvent('topbar:setTitle', {
        detail: { title: `Edit Receipt #${selected.receiptid}`, subtitle: '' }
      }));
      return;
    }
    const subtitle = meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', { detail: { title: 'Receipts ', subtitle } }));
  }, [editing, selected?.receiptid, meta?.total]);

  // Clear topbar only on unmount
  useEffect(() => {
    return () => window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
  }, []);

  function getUserFullName() {
    try {
      const u = JSON.parse(localStorage.getItem('currentUser')) || JSON.parse(localStorage.getItem('user')) || {};
      return (u.fullname || u.userFullName || u.fullName || u.name || u.login || '');
    } catch { return ''; }
  }

  const onShowReceiptHistory = (receiptId, e) => {
    if (IS_CLIENT) return;
    e.stopPropagation();
    if (!selected || selected.receiptid !== receiptId) {
      const r = receipts.find(rc => rc.receiptid === receiptId);
      if (r) setSelected(r);
    }
    setBottomPaneMode('history');
  };

  const onShowAttachments = (receiptId, e) => {
    e.stopPropagation();
    if (!selected || selected.receiptid !== receiptId) {
      const r = receipts.find(rc => rc.receiptid === receiptId);
      if (r) setSelected(r);
    }
    setBottomPaneMode('attachments');
  };

  const gotoPage = (p) => {
    const lp = Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT)));
    const clamped = Math.min(Math.max(1, p), lp);
    const nextOffset = Math.max(0, (clamped - 1) * limit);
    setReceipts([]);
    fetchReceipts(nextOffset, limit);
  };

  const handleSort = (field, e) => {
    if (rcptResizedRecently.current) return;
    if (e?.target && typeof e.target.closest === 'function' && e.target.closest('.col-resizer')) return;
    setSorts(prev => {
      const idx = prev.findIndex(s => s.field === field);
      let next = [];
      if (e?.shiftKey) {
        if (idx === -1) next = [...prev, { field, order: 'ASC' }];
        else next = prev.map((s, i) => i === idx ? { ...s, order: s.order === 'ASC' ? 'DESC' : 'ASC' } : s);
      } else {
        if (idx === 0) next = [{ field, order: prev[0].order === 'ASC' ? 'DESC' : 'ASC' }];
        else next = [{ field, order: 'ASC' }];
      }
      return next;
    });
  };

  const getPrimarySortOrder = (field) => {
    if (!field || !Array.isArray(sorts) || !sorts.length) return null;
    const primary = sorts[0];
    return primary?.field === field ? primary.order : null;
  };
  const handleInventorySort = (field, e) => { if (e?.target && typeof e.target.closest==='function' && e.target.closest('.col-resizer')) return; if (sortInventoryField === field) setSortInventoryOrder(sortInventoryOrder === 'ASC' ? 'DESC' : 'ASC'); else { setSortInventoryField(field); setSortInventoryOrder('ASC'); } };
  const renderSortIcon = (field) => sortInventoryField !== field
    ? <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />
    : (sortInventoryOrder === 'ASC'
      ? <FaSortUp style={{ color: 'var(--primary-color)', marginLeft: 4 }} />
      : <FaSortDown style={{ color: 'var(--primary-color)', marginLeft: 4 }} />);

  const onEdit = (r) => { if (IS_CLIENT) return; setSelected({ ...emptyReceipt, ...r }); setEditing(true); setCreating(false); setBottomPaneMode(null); };

  const onNew = () => {
    if (IS_CLIENT) return;
    setNewDialogOpen(true);
    setEditing(false);
    setBottomPaneMode(null);
  };

  const saveReceipt = async (eOrReceipt) => {
    // If called from onClick without wrapping, React will pass the click event as first arg
    if (eOrReceipt && typeof eOrReceipt === 'object' && typeof eOrReceipt.preventDefault === 'function') {
      try { eOrReceipt.preventDefault(); } catch {}
    }
    // Only treat the argument as a receipt if it looks like one
    const candidate = (eOrReceipt && typeof eOrReceipt === 'object' && !('preventDefault' in eOrReceipt)) ? eOrReceipt : null;

    setSaving(true);
    // IMPORTANT: When a candidate payload is provided (e.g., New Receipt dialog
    // or agent create), do NOT merge it with the current selected receipt because
    // that can accidentally carry over a prior receiptid and update the wrong row.
    // Use the candidate as‑is. Fallback to the current selection only when
    // no candidate is passed (i.e., editing in the right pane).
    const sel = candidate ? { ...candidate } : (selected || {});
    const isCreating = !(sel && sel.receiptid);

    // Robust user id fallback: try sel.userid, then localStorage user.id
    let uid = Number(sel.userid || 0);
    if (!uid) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');

        if (u && u.userid) uid = Number(u.userid);
      } catch {}
    }
    // Always assign back for consistency if missing
    if (!sel.userid && uid) sel.userid = uid;

    if (isCreating) {
      const wid = Number(sel.warehouseid || 0);
      const cid = Number(sel.clientid || 0);

      if (!wid) {
        setSaving(false);
        setWarehouseError('Please select a warehouse to create a receipt.');
        try { warehouseSelectRef.current?.focus(); } catch { }
        return;
      } else setWarehouseError('');
      if (!cid) {
        setSaving(false);
        setClientError('Please select a client to create a receipt.');
        try { clientSelectRef.current?.focus(); } catch { }
        return;
      } else setClientError('');
      if (!uid) {
        setSaving(false);
        console.log('[saveReceipt] Error: Invalid userid=', uid);
        alert('Cannot create receipt: invalid user ID.');
        return;
      }
    }

    try {
      // Determine if we're creating and normalize status
      const method = isCreating ? 'POST' : 'PUT';
      const url = isCreating ? `${API}/api/receipts` : `${API}/api/receipts/${sel.receiptid}`;
      const statusVal = (sel?.status === '' || sel?.status == null) ? undefined : Number(sel.status);

      // Whitelist only the fields the backend expects to prevent cyclic JSON errors
      const body = {
        receiptid: sel?.receiptid ?? undefined,
        warehouseid: sel?.warehouseid ?? undefined,
        date: sel?.date ?? undefined,
        userid: sel?.userid ?? undefined,
        notes: sel?.notes ?? undefined,
        clientid: sel?.clientid ?? undefined,
        // Only include statusid if editing AND a valid status was chosen
        ...( !isCreating && statusVal !== undefined ? { statusid: statusVal } : {} ),
        entrynumber: sel?.entrynumber ?? undefined,
        ponumber: sel?.ponumber ?? undefined,
        palletized: sel?.palletized ?? undefined,
        nonbillable: sel?.nonbillable ?? undefined,
        test: sel?.test ?? undefined,
        userFullName: getUserFullName()
      };

      // Enforce client locking for client users
      if (IS_CLIENT && LOCKED_CLIENT_ID) {
        body.clientid = Number(LOCKED_CLIENT_ID);
      }

      try {
        console.log('[saveReceipt] Sending (safe):', JSON.stringify(body));
      } catch {
        console.log('[saveReceipt] Sending (raw body)', body);
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        let error = 'Failed to save receipt';
        try {
          const errorData = await res.json();
          error = errorData.error || error;
        } catch { }
        console.log('[saveReceipt] Error response:', error, 'Status:', res.status);
        throw new Error(error);
      }

      const data = await res.json();
      console.log('[saveReceipt] Success:', JSON.stringify(data, null, 2));
      await fetchReceipts(meta.offset, limit);
      setSelected(null);
      setCreating(false);
      setEditing(false);
      navigate('/receipts');
    } catch (e) {
      console.log('[saveReceipt] Error:', e.message);
      alert(`Failed to save receipt: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const restoreReceipt = async (receiptid) => {
    if (!window.confirm('Restore this receipt?')) return;
    try {
      await fetchJSON(`${API}/api/receipts/${receiptid}/restore`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userFullName: getUserFullName() }),
      });
      await fetchReceipts(meta.offset, limit);
      if (selected?.receiptid === receiptid) setSelected(null);
    } catch (e) { alert(e.message); }
  };

  const deleteReceipt = async (receiptid) => {
    
    try {
      await fetchJSON(`${API}/api/receipts/${receiptid}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userFullName: getUserFullName() }),
      });
      await fetchReceipts(meta.offset, limit);
      if (selected?.receiptid === receiptid) setSelected(null);
    } catch (e) { alert(e.message); }
  };

  // === Selection helpers ===
  const toggleSelectReceipt = (id) => {
    setSelectedReceiptIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAllReceipts = () => {
    if (selectedReceiptIds.length === receipts.length) setSelectedReceiptIds([]);
    else setSelectedReceiptIds(receipts.map(r => r.receiptid));
  };

  // === Email modal helpers ===
  const closeEmailModal = () => {
    if (emailSending) return;
    setShowEmailModal(false);
    setEmailReceiptId(null);
    setEmailFields({ to: '', cc: '', bcc: '', subject: '', message: '' });
    setEmailAttachments([]);
    setEmailAttachmentSelection(new Set());
    setEmailAttachmentsLoading(false);
  };

  const sendEmailReceipts = async () => {
    const ids = (selectedReceiptIds.length && !emailReceiptId) ? selectedReceiptIds : (emailReceiptId ? [emailReceiptId] : []);
    if (!ids.length) return;
    const { to, cc, bcc, subject, message } = emailFields;
    if (!String(to || '').trim()) { alert('Please enter a recipient email'); return; }
    setEmailSending(true);
    try {
      const isBulk = ids.length > 1;
      const url = isBulk ? `${API}/api/receipts/email-bulk` : `${API}/api/receipts/${ids[0]}/email`;
      let attachmentFilenames = [];
      if (!isBulk && emailAttachmentSelection instanceof Set && emailAttachmentSelection.size > 0) {
        const selectedRecords = (emailAttachments || []).filter(
          att => att?.filename && emailAttachmentSelection.has(att.filename)
        );
        attachmentFilenames = selectedRecords.map(att => att.filename).filter(Boolean);
      }
      const payload = isBulk
        ? { receiptIds: ids, to, cc, bcc, subject, message, userFullName: getUserFullName() }
        : { to, cc, bcc, subject, message, userFullName: getUserFullName(), attachmentFilenames };
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to email receipt(s)');
      closeEmailModal();
      openNotify('success', 'Email Sent', isBulk ? 'Receipts emailed successfully.' : 'Receipt emailed successfully.');
    } catch (e) {
      const many = ids.length > 1;
      openNotify('error', 'Email Failed', `${many ? 'Failed sending receipts' : 'Failed sending receipt'} - ${e.message}`);
    } finally {
      setEmailSending(false);
    }
  };

  const handleLimitUpdate = (raw) => {
    let v = parseInt(raw ?? limitInput, 10);
    if (isNaN(v)) v = limit;
    if (v < 1) v = 1;
    if (v > 10000) v = 10000;

    setLimit(v);
    setLimitInput(String(v));
    window.localStorage.setItem('receiptsLimit', v);
    // Do not clear rows or manually fetch here; the effect watching `limit`
    // will trigger a single fetch. This avoids transient blank states and
    // duplicate in-flight requests.
  };

  const handleRowClick = (receipt) => {
    setSelected(receipt);
    setEditing(false);
    setCreating(false);
    setBottomPaneMode('inventory'); // open bottom pane with inventory by default
  };

  // Print dialog
  const confirmPrint = () => {
    if (!printForReceiptId) { setPrintDialogOpen(false); return; }
    const params = new URLSearchParams();
    if (includeMoved) params.set('includeMoved', '1');
    else params.set('originalOnly', '1');
    if (printSortBy && printSortBy !== 'default') params.set('sortBy', printSortBy);
    const qs = params.toString();
    const url = qs
      ? `${API}/api/receipts/${printForReceiptId}/pdf?${qs}`
      : `${API}/api/receipts/${printForReceiptId}/pdf`;
    window.open(url, '_blank');
    setPrintDialogOpen(false);
  };
  const confirmBulkDownload = () => {
    if (!selectedReceiptIds.length) {
      setDownloadDialogOpen(false);
      return;
    }
    const params = new URLSearchParams();
    params.set('ids', selectedReceiptIds.join(','));
    params.set('includeMoved', '1');
    params.set('inventory', downloadInventoryScope === 'current' ? 'current' : 'original');
    if (downloadInventoryScope === 'current' && toDate) {
      params.set('inventoryCutoff', toDate);
    }
    window.open(`${API}/api/receipts/pdf-bulk?${params.toString()}`, '_blank', 'noopener,noreferrer');
    setDownloadDialogOpen(false);
  };

  // Date presets
  const applyDatePreset = (preset) => {
    const today = new Date();
    let from = '', to = '';

    switch (preset) {
      case 'today': { const t = fmtYMD(today); from = t; to = t; break; }
      case 'yesterday': { const y = new Date(today); y.setDate(y.getDate() - 1); const t = fmtYMD(y); from = t; to = t; break; }
      case 'this_week': { const s = startOfWeekMon(today); from = fmtYMD(s); to = fmtYMD(today); break; }
      case 'last_week': { const s0 = startOfWeekMon(today); const s = new Date(s0); s.setDate(s0.getDate() - 7); const e = new Date(s); e.setDate(s.getDate() + 6); from = fmtYMD(s); to = fmtYMD(e); break; }
      case 'this_month': { const s = startOfMonth(today); const e = endOfMonth(today); from = fmtYMD(s); to = fmtYMD(e); break; }
      case 'last_month': { const s = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1)); const e = endOfMonth(s); from = fmtYMD(s); to = fmtYMD(e); break; }
      case 'this_year': { const s = startOfYear(today.getFullYear()); from = fmtYMD(s); to = fmtYMD(today); break; }
      case 'last_year': { const y = today.getFullYear() - 1; from = fmtYMD(startOfYear(y)); to = fmtYMD(endOfYear(y)); break; }
      case 'all': { from = ''; to = ''; break; }
      default: { localStorage.setItem('receiptsDatePreset', ''); setDatePreset(''); return; }
    }

    setFromDate(from); setToDate(to); setDatePreset(preset);
    localStorage.setItem('receiptsDatePreset', preset);

    setReceipts([]); fetchReceipts(0, limit);
  };

const createInvoiceForReceipt = async (receiptId) => {
  if (IS_CLIENT || !receiptId) return;

  // Ensure the clicked row becomes active/selected before creating
  try {
    const row = receipts.find(r => r.receiptid === receiptId);
    if (row) handleRowClick(row);
  } catch {}

  openConfirm(`Create a new invoice from Receipt #${receiptId}?`, async () => {
    try {
      const res = await fetch(`${API}/api/invoices/from-receipt/${receiptId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Explicitly set invoice date to now to avoid falling back to receipt date
        body: JSON.stringify({ userFullName: getUserFullName(), date: new Date().toISOString() }),
      });
      const j = await res.json();
      if (!res.ok || !j?.id) {
        openNotify('error', 'Invoice Creation Failed', j?.error || 'Failed to create invoice.');
        return;
      }

      // Do not refresh the list. Update the row in-place to remove the "$" icon and keep selection.
      setReceipts(prev => prev.map(r => (
        r.receiptid === receiptId
          ? { ...r, hasInvoice: 1, invoiceId: j.id, hasReceiptInvoice: 1, receiptInvoiceId: j.id }
          : r
      )));

      // Keep the user on the same row and update selected with the new invoice info
      try {
        const curr = receipts.find(r => r.receiptid === receiptId) || selected;
        if (curr) setSelected({ ...curr, hasInvoice: 1, invoiceId: j.id, hasReceiptInvoice: 1, receiptInvoiceId: j.id });
      } catch {}

      // Intentionally do not show a success notification for receipt invoice creation
      // window.location.href = `/invoice/${j.id}/edit`; // Uncomment if navigation is desired
    } catch (e) {
      openNotify('error', 'Invoice Creation Failed', `Failed to create invoice: ${e.message}`);
    }
  });
};

// Reusable confirm modal (same look/feel as Orders)
function ConfirmDialog({ open, title = 'Confirm', message = '', confirmText = 'Yes', cancelText = 'Cancel', onConfirm, onClose }) {
  if (!open) return null;
  return (
    <div className="assign-modal-overlay" onClick={onClose}>
      <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="assign-head">
          <h3>{title}</h3>
          <button className="mini-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="assign-search">
          <div style={{ color: 'var(--text-color,#eaeef5)' }}>{message}</div>
        </div>
        <div className="assign-actions">
          <button className="btn" onClick={onClose}>{cancelText}</button>
          <button
            className="btn primary"
            onClick={() => {
              try { if (typeof onConfirm === 'function') onConfirm(); }
              finally { if (typeof onClose === 'function') onClose(); }
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoticeModal({ open, type = 'success', title = '', message = '', onClose }) {
  if (!open) return null;
  const color = type === 'error' ? '#ef4444' : type === 'warn' ? '#f59e0b' : '#21bb50';
  return (
    <div className="assign-modal-overlay" onClick={onClose}>
      <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 999, background: color }} />
            {title || (type === 'error' ? 'Something went wrong' : 'Success')}
          </h3>
          <button className="mini-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="assign-search" style={{ paddingTop: 6 }}>
          <div style={{ color: 'var(--text-color,#eaeef5)' }}>{message}</div>
        </div>
        <div className="assign-actions">
          <button className="btn primary" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
}

function NewReceiptModal({ open, title = 'New Receipt', onClose, children }) {
  if (!open) return null;
  return (
    <div className="assign-modal-overlay" onClick={onClose}>
      <div
        className="assign-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1100px, 92vw)',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
          <h3>{title}</h3>
          <button className="mini-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="assign-search" style={{ paddingTop: 6, overflow: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const NewReceiptDialog = React.memo(function NewReceiptDialog({
  open,
  onClose,
  clientOptions,
  warehouseOptions,
  onSave,
  defaultWarehouseId = '',
}) {
  const [draft, setDraft] = React.useState({
    // Default to local date (YYYY-MM-DD) to avoid UTC next-day
    date: fmtYMD(new Date()),
    warehouseid: defaultWarehouseId || '',
    clientid: '',
    ponumber: '',
    entrynumber: '',
    notes: '',
    palletized: 0,
    nonbillable: 0,
    test: 0,
  });

  // Reset when the dialog opens so you start fresh each time
  React.useEffect(() => {
    if (open) {
      setDraft({
        // Reset to today's local date
        date: fmtYMD(new Date()),
        warehouseid: defaultWarehouseId || '',
        clientid: '',
        ponumber: '',
        entrynumber: '',
        notes: '',
        palletized: 0,
        nonbillable: 0,
        test: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultWarehouseId]);

  const handleSave = async () => {
    const wid = Number(draft.warehouseid || 0);
    const cid = Number(draft.clientid || 0);
    if (!wid || !cid) {
      alert('Please select a warehouse and client to create a receipt.');
      return;
    }
    const payload = {
      warehouseid: wid,
      date: draft.date,
      notes: draft.notes || '',
      clientid: cid,
      entrynumber: draft.entrynumber || '',
      ponumber: draft.ponumber || '',
      palletized: Number(draft.palletized || 0),
      nonbillable: Number(draft.nonbillable || 0),
      test: Number(draft.test || 0),
    };
    await onSave(payload);
    onClose();
  };

  return (
    <NewReceiptModal open={open} title="New Receipt" onClose={onClose}>
      <div className="receipt-editor" key="new-receipt-stable">
        <h2>New Receipt</h2>
        <div className="form-scroll">
          <div className="form-grid">
            <label>
              Client
              <select
                value={draft.clientid != null ? String(draft.clientid) : ''}
                onChange={(e) => setDraft((d) => ({ ...d, clientid: Number(e.target.value) || '' }))}
                required
              >
                <option value="">— Select —</option>
                {clientOptions.map((opt) => (
                  <option key={opt.id} value={String(opt.id)}>{opt.name}</option>
                ))}
              </select>
            </label>

            <label>
              Date
              <input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
              />
            </label>

            <label>
              Warehouse
              <select
                value={draft.warehouseid != null ? String(draft.warehouseid) : ''}
                onChange={(e) => setDraft((d) => ({ ...d, warehouseid: Number(e.target.value) || '' }))}
                required
              >
                <option value="">— Select —</option>
                {warehouseOptions.map((opt) => (
                  <option key={opt.id} value={String(opt.id)}>{opt.name}</option>
                ))}
              </select>
            </label>

            <label>
              PO Number
              <input
                name="ponumber"
                value={draft.ponumber || ''}
                onChange={(e) => setDraft((d) => ({ ...d, ponumber: e.target.value }))}
                autoComplete="off"
                inputMode="text"
              />
            </label>

            <label>
              Entry Number
              <input
                name="entrynumber"
                value={draft.entrynumber || ''}
                onChange={(e) => setDraft((d) => ({ ...d, entrynumber: e.target.value }))}
              />
            </label>

            <label className="span-2">
              Notes
              <textarea
                value={draft.notes || ''}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              />
            </label>

            <fieldset className="receipt-flags span-2">
              <legend>Receipt Options</legend>
              <label className="flag">
                <span>Arrived palletized</span>
                <input
                  type="checkbox"
                  checked={!!draft.palletized}
                  onChange={(e) => setDraft(d => ({ ...d, palletized: e.target.checked ? 1 : 0 }))}
                />
              </label>
              <label className="flag">
                <span>Non-billable</span>
                <input
                  type="checkbox"
                  checked={!!draft.nonbillable}
                  onChange={(e) => setDraft(d => ({ ...d, nonbillable: e.target.checked ? 1 : 0 }))}
                />
              </label>
            </fieldset>
          </div>
        </div>

        <div className="actions">
          <button className="btn" type="button" onClick={onClose} style={{ marginRight: 10 }}>
            <FaTimes style={{ marginRight: 6, verticalAlign: '-1px' }} />
            Close
          </button>
          <button className="btn primary" onClick={handleSave}>
            <FaRegSave size={16} /> Save Receipt
          </button>
        </div>
      </div>
    </NewReceiptModal>
  );
});


  // Editor form
  const renderEditorForm = () => {
    if (!selected) return null;
    return (
      <div className="receipt-editor">
        <h2>{!creating ? `Edit Receipt #${selected?.receiptid ?? ''}` : 'New Receipt'}</h2>

        <div className="form-scroll">
          <div className="form-grid">
            {!(IS_CLIENT && singleClientOption) && (
              <label>
                Client
                <select
                  ref={clientSelectRef}
                  value={selected?.clientid != null ? String(selected.clientid) : ''}
                  onChange={e => {
                    const val = Number(e.target.value) || '';
                    setSelected(s => ({ ...s, clientid: val }));
                    if (val) setClientError('');
                  }}
                  disabled={!creating}
                  required
                  aria-invalid={creating && !selected?.clientid ? 'true' : 'false'}
                  aria-describedby={clientError ? 'client-error' : undefined}
                  className={creating && !selected?.clientid ? 'field-error' : ''}
                >
                  <option value="">— Select —</option>
                  {clientOptions.map(opt => (
                    <option key={opt.id} value={String(opt.id)}>{opt.name}</option>
                  ))}
                </select>
                {creating && clientError && (
                  <span id="client-error" className="error-text">{clientError}</span>
                )}
              </label>
            )}

            <label>
              Date
              <input
                type="date"
                value={selected?.date ? String(selected.date).slice(0, 10) : ''}
                onChange={e => setSelected(s => ({ ...s, date: e.target.value }))}
              />
            </label>

            {!creating && (
              <label>
                Status
                <select
                  value={selected?.status ?? ''}
                  onChange={e => setSelected(s => ({ ...s, status: Number(e.target.value) }))}
                >
                  {statusOptions.map(opt => (
                    <option key={opt.statusid} value={opt.statusid}>{opt.name}</option>
                  ))}
                </select>
              </label>
            )}

            <label>
              Warehouse
              <select
                ref={warehouseSelectRef}
                value={selected?.warehouseid != null ? String(selected.warehouseid) : ''}
                onChange={e => {
                  const val = Number(e.target.value) || '';
                  setSelected(s => ({ ...s, warehouseid: val }));
                  if (val) setWarehouseError('');
                }}
                disabled={!creating}
                required
                aria-invalid={creating && !selected?.warehouseid ? 'true' : 'false'}
                aria-describedby={warehouseError ? 'warehouse-error' : undefined}
                className={creating && !selected?.warehouseid ? 'field-error' : ''}
              >
                <option value="">— Select —</option>
                {warehouseOptions.map(opt => (
                  <option key={opt.id} value={String(opt.id)}>{opt.name}</option>
                ))}
              </select>
              {creating && warehouseError && (
                <span id="warehouse-error" className="error-text">{warehouseError}</span>
              )}
            </label>

            <label>
              PO Number
              <input
                value={selected?.ponumber ?? ''}
                onChange={e => setSelected(s => ({ ...s, ponumber: e.target.value }))}
                onKeyDown={(e) => {
                  // prevent global hotkeys from firing while typing here
                  if (e.key === '=' || (e.key === '+' && e.shiftKey)) {
                    e.stopPropagation();
                  }
                }}
                autoComplete="off"
                inputMode="text"
              />
            </label>

            <label>
              Entry Number
              <input
                value={selected?.entrynumber ?? ''}
                onChange={e => setSelected(s => ({ ...s, entrynumber: e.target.value }))}
              />
            </label>

            <label className="span-2">
              Notes
              <textarea
                value={selected?.notes ?? ''}
                onChange={e => setSelected(s => ({ ...s, notes: e.target.value }))}
              />
            </label>

            <fieldset className="receipt-flags span-2">
              <legend>Receipt Options</legend>
              <label className="flag">
                <span>Arrived palletized</span>
                <input
                  type="checkbox"
                  checked={!!selected?.palletized}
                  onChange={(e) => setSelected(s => ({ ...s, palletized: e.target.checked ? 1 : 0 }))}
                />
              </label>
              <label className="flag">
                <span>Non-billable</span>
                <input
                  type="checkbox"
                  checked={!!selected?.nonbillable}
                  onChange={(e) => setSelected(s => ({ ...s, nonbillable: e.target.checked ? 1 : 0 }))}
                />
              </label>
            </fieldset>
          </div>
        </div>

        <div className="actions">
          <button className="btn" type="button" onClick={() => { setSelected(null); setCreating(false); setEditing(false); }} style={{ marginRight: 10 }}>
            Close
          </button>
          <button
            className="btn primary"
            onClick={saveReceipt}
            disabled={saving || (creating && (!selected?.warehouseid || !selected?.clientid))}
            title={
              creating && (!selected?.warehouseid || !selected?.clientid)
                ? 'Select a warehouse and client to save this receipt'
                : undefined
            }
          >
            <FaRegSave size={16} /> {saving ? 'Saving…' : 'Save Receipt'}
          </button>
        </div>
      </div>
    );
  };

  const wrapperClass =
  (selected && editing) ? 'receipts-wrapper-col with-editor' : 'receipts-wrapper-col';

  return (
    <div className={wrapperClass}>
      <div className="receipts-left-pane">
        <div className="receipts-toolbar">
          <div className="receipts-toolbar-row">
            <div className="search">
              <FaSearch />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const secret = String(q || '').trim().toLowerCase();
                    if (secret === 'danieldaniel' || secret === 'rickrick') {
                      navigate('/invaders');
                    } else if (secret === 'tomtom') {
                      navigate('/snake');
                    } else {
                      setReceipts([]); fetchReceipts(0, limit);
                    }
                  }
                }}
                placeholder={searchPlaceholder}
              />
              {q && (
                <button className="clear" onClick={() => { setQ(''); setReceipts([]); fetchReceipts(0, limit); }}>
                  <FaTimes />
                </button>
              )}
            </div>

            {!IS_CLIENT && (
              <div className="client-filter">
                <FaSearch />
                <input
                  value={clientNameFilter}
                  onChange={e => setClientNameFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setReceipts([]); fetchReceipts(0, limit);
                    }
                  }}
                  placeholder="Client name"
                />
                {clientNameFilter && (
                  <button className="clear" onClick={() => { setClientNameFilter(''); }}>
                    <FaTimes />
                  </button>
                )}
              </div>
            )}

            <div className="client-filter sku-filter">
              <FaSearch />
              <input
                value={skuFilter}
                onChange={e => setSkuFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setReceipts([]); fetchReceipts(0, limit);
                  }
                }}
                placeholder="SKU contains"
              />
              {skuFilter && (
                <button className="clear" onClick={() => { setSkuFilter(''); setReceipts([]); fetchReceipts(0, limit); }}>
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Client dropdown for client users (and optional for internal) */}
            {IS_CLIENT && !singleClientOption && (
              <div className="client-select">
                <label htmlFor="receipts-client-select" style={{ marginRight: 6 }}>Client</label>
                <select
                  id="receipts-client-select"
                  className="client-dropdown"
                  value={clientIdFilter || ''}
                  onChange={(e) => { setClientIdFilter(e.target.value); setReceipts([]); fetchReceipts(0, limit); }}
                >
                  <option value="">— All —</option>
                  {clientOptions.map(c => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Second row: Status / Date / Billed / Deleted */}
          <div className="receipts-toolbar-row">
            {/* Status multi-select (like Orders) */}
            <MultiSelectStatuses
              options={statusOptions.map(s => ({ id: Number(s.statusid ?? s.id), name: s.name }))}
              value={statusIds}
              onChange={setStatusIds}
            />

            <div className="date-filters">
              <label className="preset">
                <select
                  className="rd-input rd-select"
                  value={datePreset}
                  onChange={(e) => applyDatePreset(e.target.value)}
                  title="Quick date ranges"
                >
                  <option value="all">All Time</option>
                  <option value="">Custom…</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="this_week">This Week</option>
                  <option value="last_week">Last Week</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_year">This Year</option>
                  <option value="last_year">Last Year</option>
                </select>
              </label>

              <div className="date-input from">
                <span className="date-label">From</span>
                <input
                  type="date"
                  className="rd-input"
                  value={fromDate}
                  onChange={e => {
                    setFromDate(e.target.value);
                    setDatePreset(''); localStorage.setItem('receiptsDatePreset', '');
                    setReceipts([]); fetchReceipts(0, limit);
                  }}
                />
              </div>

              <div className="date-input to">
                <span className="date-label">To</span>
                <input
                  type="date"
                  className="rd-input"
                  value={toDate}
                  onChange={e => {
                    setToDate(e.target.value);
                    setDatePreset(''); localStorage.setItem('receiptsDatePreset', '');
                    setReceipts([]); fetchReceipts(0, limit);
                  }}
                />
              </div>
            </div>

            {/* Billed filter (internal only) */}
            {!IS_CLIENT && (
              <label style={{ display: 'flex', alignItems:'center', gap:6 }}>
                <span>Billed</span>
                <select
                  className="rd-input rd-select"
                  value={billedFilter}
                  onChange={(e) => { setBilledFilter(e.target.value); /* debounced effect will refetch */ }}
                >
                  <option value="">All</option>
                  <option value="unbilled">Unbilled only</option>
                  <option value="billed">Billed only</option>
                </select>
              </label>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={e => {
                  setShowDeleted(e.target.checked);
                  setReceipts([]); fetchReceipts(0, limit);
                }}
                style={{ marginRight: 4 }}
              />
              Show deleted
            </label>
          </div>

          <div className="toolbar-actions-row" style={{ width: '100%', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" onClick={() => { setReceipts([]); fetchReceipts(0, limit); }}>
              <FaSearch style={{ marginRight: 6 }} />
              Search
            </button>

            {!IS_CLIENT && (
              <button className="btn primary" onClick={onNew}>
                <FaPlus /> New
              </button>
            )}

            <button
              className="btn"
              disabled={selectedReceiptIds.length === 0}
              title={selectedReceiptIds.length ? `Email ${selectedReceiptIds.length} selected receipt(s)` : 'Select receipts to email'}
              onClick={() => {
                if (!selectedReceiptIds.length) return;
                setEmailReceiptId(null);
                setEmailFields({
                  to: '', cc: '', bcc: '',
                  subject: `Receipts #${selectedReceiptIds.slice(0,10).join(', #')}${selectedReceiptIds.length>10 ? '…' : ''}`,
                  message: `Please find attached ${selectedReceiptIds.length} receipt(s).`
                });
                setShowEmailModal(true);
              }}
            >
              <FaEnvelope style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Email Selected
            </button>

            <button
              className="btn"
              disabled={selectedReceiptIds.length === 0}
              title={selectedReceiptIds.length ? `Download ${selectedReceiptIds.length} selected receipt(s)` : 'Select receipts to download'}
              onClick={() => {
                if (!selectedReceiptIds.length) return;
                setDownloadDialogOpen(true);
              }}
            >
              <FaDownload style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Download Selected
            </button>

            <button
              className="btn"
              title="Export current list to CSV"
              onClick={() => {
                try {
                  const parsed = parseReceiptSearch(q);
                  const params = new URLSearchParams();
                  // Sorts (multi)
                  const sortParam = (Array.isArray(sorts) && sorts.length)
                    ? sorts.map(s => `${s.field}:${s.order}`).join(',')
                    : '';
                  if (sortParam) params.set('sort', sortParam);
                  // Filters
                  if (parsed.q) params.set('q', parsed.q);
                  if (fromDate) params.set('from', fromDate);
                  if (toDate) params.set('to', toDate);
                  if (!showDeleted) params.set('deleted', 0);
                  if (!IS_CLIENT && clientNameFilter) params.set('clientname', clientNameFilter);
                  if (IS_CLIENT && clientIdFilter) params.set('clientid', String(clientIdFilter));
                  if (Array.isArray(statusIds) && statusIds.length) params.set('statusids', statusIds.join(','));
                  if (billedFilter === 'unbilled') params.set('hasinvoice', 'unbilled');
                  else if (billedFilter === 'billed') params.set('hasinvoice', 'billed');

                  const url = `${API}/api/receipts/export?${params.toString()}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                } catch (e) {
                  alert(e?.message || 'Failed to start CSV export');
                }
              }}
            >
              <FaDownload style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Export CSV
            </button>

            <button className="btn" onClick={resetReceiptColumns} title="Reset column order and widths">
              <FaColumns style={{ marginRight: 6 }} />
              Reset Columns
            </button>
          </div>
        </div>

        {/* src/views/Receipts.jsx  (Chunk 6/6) */}
        <Split
          direction="vertical"
          sizes={showInventoryPane ? [splitSizes[0], splitSizes[1]] : [100, 0]}
          minSize={showInventoryPane ? [200, 200] : [200, 0]}
          gutterSize={showInventoryPane ? 6 : 0}
          onDragEnd={(sizes) => { setSplitSizes(sizes); localStorage.setItem('receipts-split-sizes', JSON.stringify(sizes)); }}
          style={{ height: 'calc(100vh - 150px)', minHeight: 0 }}
          className="receipts-split"
        >
          {/* Top List */}
          <div className={`receipts-list-tablecol ${rcptAutoFit ? 'auto-fit' : ''}`}>
            {loadingList ? (
              <div className="loading">Loading…</div>
            ) : (
              <>
                <table className="receipts-table" ref={rcptTableRef}>
                  <colgroup>
                    {receiptColumns.map((c, idx) => {
                      const isLast = idx === receiptColumns.length - 1;
                      // Leave last column flexible so it absorbs remaining width
                      if (rcptAutoFit) {
                        return <col key={c.key} />;
                      }
                      const w = rcptColWidths[c.key] ?? c.defaultWidth;
                      return <col key={c.key} style={isLast ? undefined : (w ? { width: `${w}px` } : undefined)} />;
                    })}
                  </colgroup>
                  <thead>
                    <tr ref={rcptHeaderRowRef}>
                      {receiptColumns.map(col => {
                        // Select column (checkbox)
                        if (col.key === 'select') {
                          return (
                            <th key={col.key} style={{ width: 28 }} data-colkey={col.key} >
                              <input
                                type="checkbox"
                                checked={receipts.length > 0 && selectedReceiptIds.length === receipts.length}
                                onChange={toggleSelectAllReceipts}
                              />
                              <span className="col-resizer" onMouseDown={(e)=>startRcptResize('select', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitRcptCol('select'); }} />
                            </th>
                          );
                        }
                        // Actions column (not sortable, not draggable)
                        if (col.key === 'actions') {
                          return (
                            <th key={col.key} data-colkey={col.key} style={{ position: 'relative' }} >
                              Actions
                              <span className="col-resizer" onMouseDown={(e)=>startRcptResize('actions', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitRcptCol('actions'); }} />
                            </th>
                          );
                        }
                        const order = col.sort ? getPrimarySortOrder(col.sort) : null;
                        return (
                          <th
                            key={col.key}
                            data-colkey={col.key}
                            onClick={col.sort ? (e)=>handleSort(col.sort, e) : undefined}
                            className={order ? 'active-sort' : ''}
                            style={{
                              cursor: col.sort ? 'pointer' : 'default',
                              userSelect: 'none',
                              position: 'relative',
                              color: order ? 'var(--primary-color)' : undefined,
                              textDecoration: order ? 'underline' : undefined,
                            }}
                            title={order ? `Sorted ${order}` : (col.sort ? 'Click to sort. Shift+Click to add secondary sort' : '')}
                            draggable
                            onDragStart={(e) => { dragRcptColKeyRef.current = col.key; e.dataTransfer.effectAllowed = 'move'; }}
                            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const fromKey = dragRcptColKeyRef.current;
                              const toKey = col.key;
                              dragRcptColKeyRef.current = null;
                              if (!fromKey || fromKey === toKey) return;
                              setReceiptColumns(prev => {
                                const fromIdx = prev.findIndex(c => c.key === fromKey);
                                const toIdx = prev.findIndex(c => c.key === toKey);
                                if (fromIdx === -1 || toIdx === -1) return prev;
                                // Prevent moving select/actions
                                if (prev[toIdx].key === 'actions' || prev[fromIdx].key === 'actions' || prev[toIdx].key === 'select' || prev[fromIdx].key === 'select') return prev;
                                const next = [...prev];
                                const [moved] = next.splice(fromIdx, 1);
                                const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;
                                next.splice(insertAt, 0, moved);
                                return next;
                              });
                            }}
                          >
                            {/* Header label with primary sort arrow */}
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: order ? 'var(--primary-color)' : undefined, textDecoration: order ? 'underline' : undefined }}>
                              {col.label || ''} {order ? (order === 'ASC' ? '▲' : '▼') : ''}
                            </span>
                            <span className="col-resizer" onMouseDown={(e)=>startRcptResize(col.key, e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitRcptCol(col.key); }} />
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.length ? (
                      receipts.map((r) => {
                        const retired = !r.deleted && /^(retired|inactive|closed)$/i.test(String(r.statusname || '').trim());
                        return (
                          <tr
                            key={r.receiptid}
                            className={`${selected?.receiptid === r.receiptid ? 'active' : ''} ${retired ? 'retired' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRowClick(r)}
                            aria-label={retired ? 'Retired receipt' : undefined}
                            title={retired ? 'This receipt is retired/inactive' : undefined}
                          >
                            {receiptColumns.map(col => (
                              <td
                                key={col.key}
                                data-colkey={col.key}
                                className={col.key === 'actions' ? 'td-actions' : undefined}
                                style={col.key === 'actions' ? { whiteSpace: 'nowrap' } : undefined}
                              >
                                {(() => {
                                  switch (col.key) {
                                    case 'select':
                                      return (
                                        <input
                                          type="checkbox"
                                          checked={selectedReceiptIds.includes(r.receiptid)}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={() => toggleSelectReceipt(r.receiptid)}
                                        />
                                      );
                                    case 'actions':
                                      return (
                                        <div className="actions">
                                          {!r.deleted ? (
                                            <>
                                              {!IS_CLIENT && !retired && (
                                                <button className="mini-btn" title="Receive Inventory" tabIndex={-1} onClick={e => { e.stopPropagation(); navigate(`/receive/${r.receiptid}`); }}>
                                                  <FaInbox size={16} />
                                                </button>
                                              )}
                                              {!IS_CLIENT && (
                                                <button className="mini-btn" title="Edit" onClick={e => { e.stopPropagation(); onEdit(r); }}>
                                                  <FaEdit size={16} />
                                                </button>
                                              )}
                                              <button className="mini-btn" title="Receipt Attachments" tabIndex={-1} onClick={(e) => onShowAttachments(r.receiptid, e)} style={{ color: r.attachmentscount > 0 ? '#21bb50' : undefined }}>
                                                <FaPaperclip size={16} style={{ color: (r.attachments_count ?? 0) > 0 ? '#21bb50' : '#000000' }} />
                                              </button>
                                              {!IS_CLIENT && (
                                                <button className="mini-btn" title="Receipt History" tabIndex={-1} onClick={e => onShowReceiptHistory(r.receiptid, e)}>
                                                  <FaHistory size={16} />
                                                </button>
                                              )}
                                              <button className="mini-btn" title="Print Receipt (PDF)" tabIndex={-1} onClick={e => { e.stopPropagation(); setPrintForReceiptId(r.receiptid); setPrintDialogOpen(true); }}>
                                                <FaPrint size={16} />
                                              </button>
                                              <button className="mini-btn" title="Email Receipt" tabIndex={-1}
                                                onClick={(e) => { e.stopPropagation(); setEmailReceiptId(r.receiptid); setShowEmailModal(true); setEmailFields({ to: '', cc: '', bcc: '', subject: `Receipt #${r.receiptid}`, message: `Please find attached Receipt #${r.receiptid}.` }); }}>
                                                <FaEnvelope size={16} />
                                              </button>
                                              {!IS_CLIENT && (
                                                ((r.hasReceiptInvoice) || (r.hasPeriodicInvoice) || (r.invoiceId)) ? (
                                                  <button
                                                    className="mini-btn"
                                                    title={(r.receiptInvoiceId || r.periodicInvoiceId || r.invoiceId) ? `Open Invoice #${r.receiptInvoiceId || r.periodicInvoiceId || r.invoiceId}` : 'Open invoice'}
                                                    tabIndex={-1}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const openId = r.receiptInvoiceId || r.periodicInvoiceId || r.invoiceId;
                                                      if (openId) window.open(`${API}/api/invoices/${openId}/pdf`, '_blank', 'noopener,noreferrer');
                                                    }}
                                                  >
                                                    <FaEye size={16} />
                                                  </button>
                                                ) : (!r.nonbillable ? (
                                                  <button
                                                    className="mini-btn"
                                                    title="Create Invoice from Receipt"
                                                    tabIndex={-1}
                                                    onClick={(e) => { e.stopPropagation(); createInvoiceForReceipt(r.receiptid); }}
                                                    style={{ color: '#21bb50' }}
                                                  >
                                                    <FaDollarSign size={16} />
                                                  </button>
                                                ) : (
                                                  <button
                                                    className="mini-btn"
                                                    title="Non-billable receipt"
                                                    tabIndex={-1}
                                                    disabled
                                                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                  >
                                                    <FaDollarSign size={16} />
                                                  </button>
                                                ))
                                              )}
                                              {!IS_CLIENT && r.statusname === 'Empty' && (
                                                <button className="mini-btn danger" title="Delete" onClick={e => { e.stopPropagation(); openConfirm(`Delete Receipt #${r.receiptid}?`, () => deleteReceipt(r.receiptid)); }}>
                                                  <FaTrash size={16} />
                                                </button>
                                              )}
                                            </>
                                          ) : (
                                            <button className="mini-btn" title="Restore" style={{ color: 'green' }} onClick={e => { e.stopPropagation(); restoreReceipt(r.receiptid); }}>
                                              <FaUndo size={16} />
                                            </button>
                                          )}
                                        </div>
                                      );
                                    case 'receiptid': return r.receiptid;
                                    case 'clientname': return r.clientname;
                                    case 'status': return (r.deleted ? (<span style={{ color: 'red', fontWeight: 600 }}>Deleted</span>) : (retired ? (<span className="status-badge retired">{r.statusname || ''}</span>) : (r.statusname || '')));
                                    case 'createdby': return (r.createdby || '');
                                    case 'date': return (r.date ? new Date(r.date).toLocaleDateString() : '');
                                    case 'warehouse': return r.warehousename;
                                    case 'ponumber': return r.ponumber;
                                    case 'entrynumber': return r.entrynumber;
                                    case 'notes': return r.notes || '';
                                    default: return r[col.key] ?? '';
                                  }
                                })()}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    ) : (
                      <tr><td colSpan={receiptColumns.length}>No receipts</td></tr>
                    )}
                  </tbody>
                </table>

                <div className="pagination">
                  <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>&laquo;</button>
                  <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>Prev</button>
                  <span>Page {page} of {lastPage}</span>
                  <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(page + 1)}>Next</button>
                  <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(lastPage)}>&raquo;</button>
                  <label style={{ marginLeft: 12, fontSize: 13 }}>
                    Rows per page:&nbsp;
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={limitInput}
                      style={{ width: 60, fontSize: 13, padding: "2px 4px" }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          setIsTypingLimit(false);
                          handleLimitUpdate(e.currentTarget.value);
                        } else {
                          setIsTypingLimit(true);
                        }
                      }}
                      onChange={e => {
                        setLimitInput(e.target.value);
                        // Commit immediately on spinner changes as well; since
                        // handleLimitUpdate no longer triggers a fetch directly,
                        // this will rely on the `limit` effect and avoid blanks.
                        handleLimitUpdate(e.target.value);
                      }}
                      onBlur={e => {
                        setIsTypingLimit(false);
                        handleLimitUpdate(e.target.value);
                      }}
                    />
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Bottom Pane: Inventory, History, Attachments */}
          <div className="receipt-inventory-pane">
            {bottomPaneMode === 'inventory' && selected && (
              <>
                <div className="bottom-pane-toolbar">
                  <button
                    className={`btn toggle ${bottomPaneMode === 'inventory' ? 'active' : ''}`}
                    onClick={() => setBottomPaneMode('inventory')}
                    aria-pressed={bottomPaneMode === 'inventory'}
                    title="Inventory"
                  >
                    <FaInbox style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>Inventory</span>
                  </button>
                  {!IS_CLIENT && (
                    <button
                      className={`btn toggle ${bottomPaneMode === 'history' ? 'active' : ''}`}
                      onClick={() => setBottomPaneMode('history')}
                      aria-pressed={bottomPaneMode === 'history'}
                      title="History"
                    >
                      <FaHistory style={{ verticalAlign: '-2px' }} />
                      <span style={{ marginLeft: 6 }}>History</span>
                    </button>
                  )}
                  <button
                    className={`btn toggle ${bottomPaneMode === 'attachments' ? 'active' : ''}`}
                    onClick={() => setBottomPaneMode('attachments')}
                    aria-pressed={bottomPaneMode === 'attachments'}
                    title="Attachments"
                  >
                    <FaPaperclip style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>Attachments</span>
                  </button>
                  <div style={{ flex: 1 }} />
                  {!IS_CLIENT && (
                    <button className="btn" onClick={printPalletLabels} title="Pallet Labels">
                      <FaTag style={{ marginRight: 6 }} /> Pallet Labels
                    </button>
                  )}
                  <button className="btn" onClick={() => setBottomPaneMode(null)} title="Close">
                    <FaTimes style={{ marginRight: 6 }} /> Close
                  </button>
                </div>
                <h2>Inventory for Receipt #{selected?.receiptid ?? ''}</h2>
                <div className="receipt-inventory-table-wrapper">
                  <table className="receipt-inventory-table" ref={rcptInvTableRef}>
                    <colgroup>
                      {RCPT_INV_COLS.map(k => {
                        const w = rcptInvColWidths[k] ?? DEFAULT_RCPT_INV_WIDTHS[k];
                        return <col key={k} style={{ width: w ? `${w}px` : undefined }} />;
                      })}
                    </colgroup>
                    <thead>
                      <tr ref={rcptInvHeaderRef}>
                        <th style={{ width: 34, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={sortedInventoryRows.length > 0 && sortedInventoryRows.every(r => selectedInvIds.has(r.inventoryid))}
                            onChange={(e) => toggleAllInv(e.target.checked)}
                          />
                          <span className="col-resizer" onMouseDown={(e)=>startRcptInvResize('select', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} />
                        </th>
                        {[
                          ['pallet_number','Storage Unit #'],
                          ['recvdate','Recv Date'],
                          ['storage_device','Storage Device/Type'],
                          ['sku','SKU'],
                          ['item_description','Item Description'],
                          ['location','Location'],
                          ['warehouse_area','Warehouse Area'],
                          ['description','Inventory Notes/Description'],
                          ['partnumber','Part Number'],
                          ['expiration','Expiration'],
                          ['lotnum','Lot #'],
                          ['serial','Serial Number'],
                          ['ponum','PO'],
                          ['packaging','Packaging Unit Type'],
                          ['qtyunits','Qty Boxes Received'],
                          ['qtyperunit','Qty Items Per'],
                          ['pieces','Qty Pieces Received'],
                          ['qtyunitsonhand','Qty Unit Available'],
                          ['piecesonhand','Items/Units Avail'],
                          ['unit_volume','Unit Volume'],
                          ['total_units_volume','Total Units Volume'],
                          ['item_volume','Item Volume'],
                          ['available_item_volume','Available Item Volume'],
                        ].map(([key,label]) => (
                          <th
                            key={key}
                            onClick={(e)=>handleInventorySort(key, e)}
                            className={sortInventoryField === key ? 'active-sort' : ''}
                            style={{
                              position: 'relative',
                              color: sortInventoryField === key ? 'var(--primary-color)' : undefined,
                              textDecoration: sortInventoryField === key ? 'underline' : undefined,
                            }}
                          >
                            {label} {renderSortIcon(key)}
                            <span className="col-resizer" onMouseDown={(e)=>startRcptInvResize(key, e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inventoryLoading ? (
                        <tr><td colSpan={24}>Loading…</td></tr>
                      ) : sortedInventoryRows.length ? (
                        sortedInventoryRows.map(row => {
                          const retired = !!row.retiredate;
                          return (
                          <tr
                            key={row.inventoryid}
                            className={`${activeInvRowId === row.inventoryid ? 'active' : ''} ${retired ? 'retired' : ''}`}
                            onClick={() => setActiveInvRowId(row.inventoryid)}
                          >
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedInvIds.has(row.inventoryid)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleInvSelected(row.inventoryid)}
                              />
                            </td>
                            <td>{row.pallet_number}</td>
                            <td>{row.recvdate ? new Date(row.recvdate).toLocaleDateString() : ''}</td>
                            <td>{row.storage_device}</td>
                            <td>{row.sku}</td>
                            <td>{row.item_description}</td>
                            <td className='nowrap'>
                              {(() => {
                                const loc = String(row.location ?? '').trim();
                                const lt = String(row.pallet_locationtypeid ?? row.locationtypeid ?? '').trim();
                                const stack = String(row.stack ?? '').trim();
                                if (lt === '1' || loc.toLowerCase() === 'floor') {
                                  const stackLabel = stack ? stack : 'Stack';
                                  return `FLOOR - ${stackLabel}`;
                                }
                                return loc;
                              })()}
                            </td>
                            <td>{row.warehouse_area}</td>
                            <td>{row.description}</td>
                            <td>{row.partnumber}</td>
                            <td>{row.expiration ? new Date(row.expiration).toLocaleDateString() : ''}</td>
                            <td>{row.lotnum}</td>
                            <td>{row.serial || ''}</td>
                            <td>{row.ponum}</td>
                            <td>{row.packaging}</td>
                            <td>{row.qtyunits}</td>
                            <td>{row.qtyperunit}</td>
                            <td>{(() => { const d = Number(row.pieces); if (Number.isFinite(d) && d > 0) return d; const u=Number(row.qtyunits||0), per=Number(row.qtyperunit||0); const calc=u*per; return Number.isFinite(calc)?calc:0; })()}</td>
                            <td>{row.qtyunitsonhand}</td>
                            <td>{row.piecesonhand}</td>
                            <td>{row.unit_volume ?? ''}</td>
                            <td>{row.total_units_volume ?? ''}</td>
                            <td>{row.item_volume ?? ''}</td>
                            <td>{row.available_item_volume ?? ''}</td>
                          </tr>);
                        })
                      ) : (
                        <tr><td colSpan={24}>No inventory found for this receipt.</td></tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr style={{ color: 'var(--text-color)', textAlign: 'center' }}>
                        <td></td>
                        <td>
                          <strong>Total Pallets: {uniquePalletNumbers}</strong>
                        </td>
                        {RCPT_INV_COLS.slice(2).map((k) => {
                          let content = null;
                          if (k === 'qtyunits') content = <strong>{invTotals.qtyunits.toLocaleString()}</strong>;
                          if (k === 'pieces') content = <strong>{invTotals.pieces.toLocaleString()}</strong>;
                          if (k === 'qtyunitsonhand') content = <strong>{invTotals.qtyunitsonhand.toLocaleString()}</strong>;
                          if (k === 'piecesonhand') content = <strong>{invTotals.piecesonhand.toLocaleString()}</strong>;
                          return <td key={`tf-${k}`}>{content}</td>;
                        })}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}

            {bottomPaneMode === 'history' && selected && (
              <>
                <div className="bottom-pane-toolbar">
                  <button className={`btn toggle ${bottomPaneMode === 'inventory' ? 'active' : ''}`} onClick={() => setBottomPaneMode('inventory')} aria-pressed={bottomPaneMode === 'inventory'} title="Inventory">
                    <FaInbox style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>Inventory</span>
                  </button>
                  {!IS_CLIENT && (
                    <button className={`btn toggle ${bottomPaneMode === 'history' ? 'active' : ''}`} onClick={() => setBottomPaneMode('history')} aria-pressed={bottomPaneMode === 'history'} title="History">
                      <FaHistory style={{ verticalAlign: '-2px' }} />
                      <span style={{ marginLeft: 6 }}>History</span>
                    </button>
                  )}
                  <button className={`btn toggle ${bottomPaneMode === 'attachments' ? 'active' : ''}`} onClick={() => setBottomPaneMode('attachments')} aria-pressed={bottomPaneMode === 'attachments'} title="Attachments">
                    <FaPaperclip style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>Attachments</span>
                  </button>
                  <div style={{ flex: 1 }} />
                  <button className="btn" onClick={() => setBottomPaneMode(null)} title="Close">
                    <FaTimes style={{ marginRight: 6 }} /> Close
                  </button>
                </div>

                <h2>History for Receipt #{selected?.receiptid ?? ''}</h2>
                {historyLoading ? (
                  <div className="loading">Loading history…</div>
                ) : (
                  <div className="history-table-wrapper">
                    <table className="history-table">
                      <thead>
                        <tr><th>Date</th><th>User</th><th>Log</th></tr>
                      </thead>
                      <tbody>
                        {history.length ? history.map(h => (
                          <tr key={h.id}>
                            <td>{h.date ? new Date(h.date).toLocaleString() : ''}</td>
                            <td>{h.username ?? ''}</td>
                            <td>{h.log}</td>
                          </tr>
                        )) : (
                          <tr><td colSpan={3}>No history entries</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {bottomPaneMode === 'attachments' && selected && (
              <div className="receipt-attachments-panel">
                <div className="bottom-pane-toolbar">
                  <button className={`btn toggle ${bottomPaneMode === 'inventory' ? 'active' : ''}`} onClick={() => setBottomPaneMode('inventory')} aria-pressed={bottomPaneMode === 'inventory'} title="Inventory">
                    <FaInbox style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>Inventory</span>
                  </button>
                  {!IS_CLIENT && (
                    <button className={`btn toggle ${bottomPaneMode === 'history' ? 'active' : ''}`} onClick={() => setBottomPaneMode('history')} aria-pressed={bottomPaneMode === 'history'} title="History">
                      <FaHistory style={{ verticalAlign: '-2px' }} />
                      <span style={{ marginLeft: 6 }}>History</span>
                    </button>
                  )}
                  <button className={`btn toggle ${bottomPaneMode === 'attachments' ? 'active' : ''}`} onClick={() => setBottomPaneMode('attachments')} aria-pressed={bottomPaneMode === 'attachments'} title="Attachments">
                    <FaPaperclip style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>Attachments</span>
                  </button>
                  <div style={{ flex: 1 }} />
                  <button className="btn" onClick={() => setBottomPaneMode(null)} title="Close">
                    <FaTimes style={{ verticalAlign: '-2px', marginRight: 6 }} /> Close
                  </button>
                </div>

                <h2>Attachments for Receipt #{selected?.receiptid ?? ''}</h2>

                <div className="attachments-toolbar">
                  <div className={`dropzone ${isDragging ? 'dragging' : ''}`} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} title="Drop files here">
                    Drop files here
                  </div>
                  <div className="attach-actions">
                    <input type="text" className="attach-desc" placeholder="Description (optional)" value={attachDesc} onChange={e => setAttachDesc(e.target.value)} />
                    <input type="file" multiple ref={fileInputRef} style={{ display: 'none' }} onChange={e => { const files = Array.from(e.target.files || []); if (files.length) setPendingFiles(files); if (fileInputRef.current) fileInputRef.current.value = ''; }} />
                    <button className="btn" onClick={() => fileInputRef.current?.click()}><FaFolderOpen style={{ verticalAlign: '-2px' }} /> Browse…</button>
                    <button className="btn" onClick={() => fetchReceiptAttachments(selected?.receiptid)} title="Refresh"><FaSync style={{ verticalAlign: '-2px' }} /></button>
                    <button className="btn primary" disabled={!pendingFiles.length} onClick={uploadFiles} title={pendingFiles.length ? `Upload ${pendingFiles.length} file(s)` : 'Choose files first'}><FaUpload style={{ verticalAlign: '-2px' }} />Upload</button>
                  </div>
                </div>

                {!!pendingFiles.length && (
                  <div className="pending-files">
                    {pendingFiles.map((f, i) => (
                      <span key={f.name + i} className="file-chip">
                        {f.name}
                        <button type="button" className="chip-x" title="Remove" aria-label={`Remove ${f.name}`} onClick={() => removePendingFile(i)}>×</button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="table-scroll-wrapper">
                  <table className="attachments-table" style={{ width: '100%', background: 'transparent' }}>
                    <thead>
                      <tr>
                        <th className="actions" style={{ width: 72 }}>Action</th>
                        <th>Filename</th>
                        <th>Date</th>
                        <th className="desc">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachmentsLoading ? (
                        <tr><td colSpan={4}>Loading…</td></tr>
                      ) : attachments.length ? (
                        attachments.map(a => {
                          const name = (a.filename || '').split('/').pop();
                          return (
                            <tr key={a.id ?? name}>
                              <td className="actions">
                                {a.id ? (
                                  <button className="mini-btn danger" title="Delete attachment" onClick={() => deleteAttachment(a.id)}>
                                    <FaTrash size={16} />
                                  </button>
                                ) : null}
                              </td>
                              <td><a href={`${API}/api/receipts/${selected?.receiptid}/attachments/${encodeURIComponent(name)}`} target="_blank" rel="noopener noreferrer">{name}</a></td>
                              <td>{a.date ? new Date(a.date).toLocaleString() : ''}</td>
                              <td className="desc">{a.description || ''}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan={4} style={{ color: '#aaa' }}>No attachments</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Split>
      </div>

      {/* no overlay; secret route navigates to /invaders */}

      {/* RIGHT PANE (Edit only) */}
      {!IS_CLIENT && (selected && editing) && (
        <div className="receipts-right-pane">
          <ReceiptEditWorkspace receipt={selected} editor={renderEditorForm()} />
        </div>
      )}

      {/* New Receipt Modal */}
      {!IS_CLIENT && (
        <NewReceiptDialog
          open={newDialogOpen}
          onClose={() => setNewDialogOpen(false)}
          clientOptions={clientOptions}
          warehouseOptions={warehouseOptions}
          defaultWarehouseId={warehouseOptions[0]?.id || ''}
          onSave={saveReceipt}
        />
      )}

      {/* Print options dialog */}
      {printDialogOpen && (
        <div className="modal-backdrop" onClick={() => setPrintDialogOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="printOptionsTitle" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: '90vw', height: 'auto' }}>
            <div className="modal-header">
              <h3 id="printOptionsTitle">Print Options</h3>
              <button className="icon-btn" onClick={() => setPrintDialogOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="help-section" style={{ marginTop: 0 }}>
                <p>Choose which inventory to include in the PDF for WR#{printForReceiptId ?? ''}:</p>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input type="radio" name="print_inv_choice" checked={!includeMoved} onChange={() => setIncludeMoved(false)} />
                  <span><b>Original received inventory only</b></span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" name="print_inv_choice" checked={includeMoved} onChange={() => setIncludeMoved(true)} />
                  <span><b>Include any moved / consolidated inventory</b></span>
                </label>
              </div>
              <div className="help-section" style={{ marginTop: 16 }}>
                <p>Sort inventory rows by:</p>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <input type="radio" name="print_sort_choice" checked={printSortBy === 'default'} onChange={() => setPrintSortBy('default')} />
                  <span>Default (Pallet / Received date)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <input type="radio" name="print_sort_choice" checked={printSortBy === 'storage'} onChange={() => setPrintSortBy('storage')} />
                  <span>Storage Unit #</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" name="print_sort_choice" checked={printSortBy === 'sku'} onChange={() => setPrintSortBy('sku')} />
                  <span>SKU</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setPrintDialogOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={confirmPrint}>Print</button>
            </div>
          </div>
        </div>
      )}

      {downloadDialogOpen && (
        <div className="modal-backdrop" onClick={() => setDownloadDialogOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="downloadOptionsTitle" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: '90vw', height: 'auto' }}>
            <div className="modal-header">
              <h3 id="downloadOptionsTitle">Download Options</h3>
              <button className="icon-btn" onClick={() => setDownloadDialogOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="help-section" style={{ marginTop: 0 }}>
                <p>Choose which inventory snapshot to include for {selectedReceiptIds.length} selected receipt{selectedReceiptIds.length === 1 ? '' : 's'}:</p>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input
                    type="radio"
                    name="download_inv_choice"
                    checked={downloadInventoryScope !== 'current'}
                    onChange={() => setDownloadInventoryScope('original')}
                  />
                  <span><b>Originally received inventory</b> (uses the quantities received on the WR)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="download_inv_choice"
                    checked={downloadInventoryScope === 'current'}
                    onChange={() => setDownloadInventoryScope('current')}
                  />
                  <span><b>Inventory levels as of requested date(s)</b> </span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setDownloadDialogOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={confirmBulkDownload}>Download</button>
            </div>
          </div>
        </div>
      )}

            {showEmailModal && (
        <div className="modal-overlay" onClick={() => !emailSending && closeEmailModal()}>
          <div className="modal receipts-email-modal" onClick={e => e.stopPropagation()}>
            <h2>
              {emailReceiptId ? `Email Receipt #${emailReceiptId}` : `Email ${selectedReceiptIds.length} Receipt${selectedReceiptIds.length===1?'':'s'}`}
            </h2>
            <div className="payment-form">
              <div className="form-grid">
                <label>
                  <span>To</span>
                  <input type="email" value={emailFields.to} onChange={e => setEmailFields({ ...emailFields, to: e.target.value })} placeholder="customer@example.com" />
                </label>
                <label>
                  <span>CC</span>
                  <input type="text" value={emailFields.cc} onChange={e => setEmailFields({ ...emailFields, cc: e.target.value })} placeholder="optional" />
                </label>
                <label>
                  <span>BCC</span>
                  <input type="text" value={emailFields.bcc} onChange={e => setEmailFields({ ...emailFields, bcc: e.target.value })} placeholder="optional" />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  <span>Subject</span>
                  <input type="text" value={emailFields.subject} onChange={e => setEmailFields({ ...emailFields, subject: e.target.value })} placeholder={emailReceiptId ? `Receipt #${emailReceiptId}` : `Receipts #${selectedReceiptIds[0] || ''}`} />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  <span>Message</span>
                  <textarea rows={6} value={emailFields.message} onChange={e => setEmailFields({ ...emailFields, message: e.target.value })} placeholder="Add a short message..." />
                </label>
              </div>
              {emailSingleReceiptId && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong>Attachments</strong>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setEmailAttachmentSelection(new Set((emailAttachments || []).map(a => a?.filename).filter(Boolean)))}
                        disabled={emailAttachmentsLoading || !emailAttachments.length}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setEmailAttachmentSelection(new Set())}
                        disabled={emailAttachmentsLoading || !emailAttachments.length}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  {emailAttachmentsLoading ? (
                    <div style={{ fontSize: 13, opacity: 0.85 }}>Loading attachments…</div>
                  ) : emailAttachments.length ? (
                    <>
                      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #444', borderRadius: 6 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ width: 50, textAlign: 'left', padding: '6px 8px' }}>Send</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Description</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Filename</th>
                              <th style={{ width: 180, textAlign: 'left', padding: '6px 8px' }}>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {emailAttachments.map((att, idx) => {
                              const fullKey = att?.filename || '';
                              const displayName = fullKey ? fullKey.split(/[/\\]/).pop() : `file-${idx + 1}`;
                              const checked = fullKey ? emailAttachmentSelection.has(fullKey) : false;
                              const dt = att?.date ? new Date(att.date) : null;
                              const dateText = dt && !isNaN(dt.getTime()) ? dt.toLocaleString() : '';
                              const href = `${API}/api/receipts/${emailSingleReceiptId}/attachments/${encodeURIComponent(displayName || '')}`;
                              return (
                                <tr key={`${fullKey || displayName}-${idx}`}>
                                  <td style={{ padding: '4px 8px' }}>
                                    <input
                                      type="checkbox"
                                      disabled={!fullKey}
                                      checked={checked}
                                      onChange={(e) => setEmailAttachmentSelection(prev => {
                                        const next = new Set(prev);
                                        if (!fullKey) return next;
                                        if (e.target.checked) next.add(fullKey);
                                        else next.delete(fullKey);
                                        return next;
                                      })}
                                    />
                                  </td>
                                  <td style={{ padding: '4px 8px' }}>{att?.description || ''}</td>
                                  <td style={{ padding: '4px 8px' }}>
                                    {displayName ? (
                                      <a href={href} target="_blank" rel="noopener noreferrer">{displayName}</a>
                                    ) : 'Attachment'}
                                  </td>
                                  <td style={{ padding: '4px 8px' }}>{dateText}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                        Receipt PDF is always attached. Select additional files to include.
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 13, opacity: 0.85 }}>No attachments found for this receipt.</div>
                  )}
                </div>
              )}
              <div className="modal-actions">
                <button className="btn" onClick={closeEmailModal} disabled={emailSending}>
                  <FaTimes style={{ marginRight: 6 }} />
                  Cancel
                </button>
                <button className="btn primary" onClick={sendEmailReceipts} disabled={emailSending}>
                  {emailSending ? 'Sending…' : (
                    <>
                      <FaPaperPlane style={{ marginRight: 6 }} />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legacy print-help dialog */}
      {showPrintOpts && (
        <div className="modal-printhelp-backdrop" onClick={() => setShowPrintOpts(false)}>
          <div className="modal-printhelp" role="dialog" aria-modal="true" aria-labelledby="printOptionsTitle" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-printhelp-header">
              <h3 id="printOptionsTitle">Print Receipt #{printForReceipt?.receiptid ?? ''}</h3>
              <button className="icon-btn" onClick={() => setShowPrintOpts(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-printhelp-body">
              <div className="help-section" style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, marginRight: 8 }}>Choose inventory to include</div>
                <PrintHelpDialog />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <input type="radio" name="print-scope" checked={!includeMoved} onChange={() => setIncludeMoved(false)} />
                Original received inventory only
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="radio" name="print-scope" checked={includeMoved} onChange={() => setIncludeMoved(true)} />
                Include any moved / consolidated inventory
              </label>
            </div>
            <div className="modal-printhelp-footer">
              <button className="btn" onClick={() => setShowPrintOpts(false)}>Cancel</button>
              <button className="btn primary" onClick={() => {
                const id = printForReceipt?.receiptid;
                if (id) window.open(`${API}/api/receipts/${id}/pdf`, '_blank');
                setShowPrintOpts(false);
              }}>
                Print PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        onClose={closeConfirm}
        onConfirm={() => {
          const fn = confirmState.onConfirm;
          if (typeof fn === 'function') fn();
        }}
      />

      <NoticeModal
        open={notify.open}
        type={notify.type}
        title={notify.title}
        message={notify.message}
        onClose={closeNotify}
      />

      
    </div>
  );
} 
